/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Do not import EzEnumerations.js into this class to avoid circular references.

    The classes EzDialogConfig, EzDialogResponseStatus, and EzDialogResponse
    have moved from within this module to their own files. Below you can find the
    new locations for the classes:

    * EzDialogConfig: import { EzDialogConfig } from '/ezlibrary/ux/dialogs/EzDialogConfig.js';
    * EzDialogResponseStatus: import { EzDialogResponseStatus } from '/ezlibrary/ux/dialogs/EzDialogResponseStatus.js';
    * EzDialogResponse: import { EzDialogResponse } from '/ezlibrary/ux/dialogs/EzDialogResponse.js';

    For backwards compatibility, this module still exports the classes. However,
    the exports will get removed in a future release. Please migrate to using
    the new locations as soon as possible.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import {
    EzException,
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzArray,
    EzFunction,
    EzHtml,
    EzUrl,
    EzPromise,
    EzJson
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzErrorCode,
    EzRegistrationState,
    EzDialogResponseStatus
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';
import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
import { EzDialogConfig } from '/ezlibrary/ux/dialogs/EzDialogConfig.js';
import { EzDialogResponse } from '/ezlibrary/ux/dialogs/EzDialogResponse.js';

import { EzUx } from '/ezlibrary/ux/EzUx.js';
import { EzUI } from '/public/javascript/common/ezui.js';
import { EzClockerDialog } from '/ezlibrary/ux/dialogs/EzClockerDialog.js';

const EzDialogSizes = {
    widths: {
        default: 500,
        auto: 'auto',
        w1024: 1024,
        w800: 800,
        w600: 600
    },
    heights: {
        default: 'auto',
        auto: 'auto',
        h200: 200,
        h300: 300,
        h500: 500,
        h550: 550,
        h600: 600
    }
};

/**
 * @class
 * @extends {EzClass}
 * @description
 * Controller and View management for ezClocker dialogs.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzDialog } from '/public/javascript/common/ezclocker-dialog-helper.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Class ready for check:
 *     ezApi?.ezclocker?.ezDialog?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen for class onReady event:
 *     EzClass.ezWaitReady(
 *         EzDialog.ezEventNames.onReady,
 *         this.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
class EzDialog extends EzClass {
    /**
     * @static
     * @public
     * @function
     * Gets the HTML mailto link for ezClocker support.
     */
    static ezGenerateMailToSupportLinkHtml(optionalSubject, optionalBody) {
        let mailtoUri = 'mailto:support@ezclocker.com';

        let paramChar = '?';

        if (EzString.hasLength(optionalSubject)) {
            mailtoUri = EzUrl.build`
                ${mailtoUri}
                ${paramChar}subject=${optionalSubject}`;

            paramChar = '&';
        }

        if (EzString.hasLength(optionalBody)) {
            mailtoUri = EzUrl.build`
                ${mailtoUri}
                ${paramChar}body=${optionalBody}`;
        }

        return EzHtml.build`
            <a
                href="${mailtoUri}"
                class="ezLink-ezClockerNavy">
                support@ezclocker.com
            </a>`;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a message instructing the user to try refreshing the page after
     * a few miniutes and then contact support.
     * @returns {string}
     */
    static get REFRESH_PAGE_THEN_REPORT() {
        return EzString.msg`
            Please try refreshing the page in a few minutes.
            If the error continues please report the error to
            ${EzDialog.ezGenerateMailToSupportLinkHtml()} for additional assistance.`;
    }

    /**
     * @public @readonly @property
     * Gets the try again then report problem message
     * @returns {string}
     * EzString.msg`
     *      Please try again in a few minutes.
     *      If this error persists after multiple attempts, please report the error to ezClocker support.`
     */
    get TRY_AGAIN_THEN_REPORT() {
        return EzString.msg`
            Please try again in a few minutes.
            If this error persists after multiple attempts, please report the error to ezClocker support.`;
    }

    /**
     * @private @field
     * Stores a map of commonly used image id = Image url
     * @type {object}
     */
    #ezDialogImageUrls = null;
    /**
     * @public @readonly @property
     * Gets a map of image id = Image url
     * @returns {object}
     */
    get ezDialogImageUrls() {
        if (null == this.#ezDialogImageUrls) {
            this.#ezDialogImageUrls = {
                infoImageUrl: globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicImagesUrl('icons/info.svg'),
                questionImageUrl: globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicImagesUrl('icons/question-ezclocker-green.svg'),
                warnImageUrl: globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicImagesUrl('icons/warn-triangle-orange.svg'),
                errorImageUrl: globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicImagesUrl('icons/alert-dark-red.svg')
            };
        }

        return this.#ezDialogImageUrls;
    }

    /**
     * @public @readonly @property
     * Gets the employee invite tipe constant
     * @returns {string}
     * 'EMPLOYEE_INVITE_TIP'
     */
    get EMPLOYEE_INVITE_TIP() {
        return 'EMPLOYEE_INVITE_TIP';
    }

    /**
     * @public @readonly @property
     * Gets the employee pin tip constant
     * @returns {string}
     * 'EMPLOYEE_PIN_TIP'
     */
    get EMPLOYEE_PIN_TIP() {
        return 'EMPLOYEE_PIN_TIP';
    }

    /**
     * @public @readonly @property
     * Gets the ezClocker error dialog HTML element id constant
     * @returns {string}
     * 'EzErrorDialog'
     */
    get EZ_ERROR_DIALOG_ID() {
        return 'EzErrorDialog';
    }

    /**
     * @public
     * @function
     * Initializes EzDialog
     * @returns {EzDialog}
     */
    ezInit() {
        return EzDialog.ezInstance;
    }

    /**
     * @public
     * @function
     * Appends the hidden dialogs container with id EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID to the
     * <body> element of the document if it does not already exist.
     */
    ezInjectHiddenDialogContainer() {
        if (!EzUx.elementExists(EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID)) {
            EzUx.appendContent(
                'body',
                EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID);
        }
    }

    /**
     * @public
     * @function
     * Appends the provied dialog's html into the hidden dialog container of the document.
     * @param {string} dialogHtml
     * HTML for the dialog to inject
     */
    ezInitAndInjectDialog(ezDialogConfig) {
        if (!EzObject.isValid(ezDialogConfig)) {
            throw new EzBadParamException(
                'ezDialogConfig',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezInjectHiddenDialog);
        }

        EzDialog.ezInstance.ezInjectHiddenDialogContainer();

        EzUx.appendContent(
            EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID,
            ezDialogConfig.ezDialogContentHtml);

        EzUx.$id(ezDialogConfig.ezDialogId).dialog(ezDialogConfig);
    }

    /**
     * @public
     * @function
     * Builds a jQuery ui dialog from the information provided in ezDialogConfig.
     * Requires a value in ezDialogContentHtml.
     * @param {EzDialogConfig} ezDialogConfig
     * @returns {object}
     * Returns the jQuery ui dialog reference.
    */
    ezInitDialogTemplateFromDialogConfig(ezDialogConfig) {
        if (!EzObject.isValid(ezDialogConfig)) {
            throw new EzBadParamException(
                'ezDialogConfig',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezInitDialogTemplateFromDialogConfig);
        }
        if ('EzDialogConfig' !== ezDialogConfig.constructor.name) {
            throw new EzBadParamException(
                'ezDialogConfig (class EzDialogConfig)',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezInitDialogTemplateFromDialogConfig);
        }
        if (!EzString.hasLength(ezDialogConfig.ezDialogContentHtml)) {
            throw new EzBadParamException(
                'ezDialogConfig.ezDialgoContentHtml',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezInitDialogTemplateFromDialogConfig);
        }

        if (!EzString.hasLength(ezDialogConfig.ezDialogId)) {
            ezDialogConfig.ezDialogId = `EzClockerDialogTemplate_${globalThis.ezApi.ezclocker.ezDateTime.ezNowAsTimestamp()}`;
        }

        let dialogHtml = EzHtml.build`
            <div
                id="${ezDialogConfig.ezDialogId}"
                style="display:none"`;

        dialogHtml = EzString.hasLength(ezDialogConfig.ezDialogIconUrl)
            ? EzHtml.build`
                ${dialogHtml}
                    class="ezDialog-with-icon">
                    <div
                        id="${ezDialogConfig.ezDialogId}_IconContainer"
                        class="ezDialog-icon-container">
                        <img
                            id="${ezDialogConfig.ezDialogId}_Image"
                            class="ezDialog-icon"
                            src="${ezDialogConfig.ezDialogIconUrl}"/>
                    </div>`
            : EzHtml.build`
                ${dialogHtml}
                    class="ezDialog-without-icon">`;

        dialogHtml = EzHtml.build`
            ${dialogHtml}
                <div
                    id="${ezDialogConfig.ezDialogId}_ContentContainer"
                    class="ezDialog-content-container">
                    ${ezDialogConfig.ezDialogContentHtml}
                </div>
            </div>`;

        EzUx.appendContent(
            'body',
            dialogHtml);

        return EzDialog.ezInitDialogWithConfig(
            ezDialogConfig.ezDialogId,
            ezDialogConfig);
    }

    /**
     * @public
     * @function
     * Builds the basic ezClocker dialog's HTML
     * @returns {string}
    */
    ezBuildDialogFromTemplate(dialogId, imgUrl, dialogContent) {
        if (!EzString.hasLength(dialogId)) {
            dialogId = `EzClockerDialog_${globalThis.ezApi.ezclocker.ezDateTime.ezNowAsTimestamp()}`;
        }

        const dialogImage = EzString.hasLength(imgUrl)
            ? EzHtml.build`
                <td
                    id="${dialogId}_IconImageContainer"
                    class="ezClockerDialogImageCell">
                    <img
                        id="${dialogId}_IconImage"
                        class="ezClockerDialogImage"
                        src="${imgUrl}"/>
                </td>`
            : EzString.EMPTY;

        return EzHtml.build`
            <div
                id="${dialogId}"
                class="ezClockerDialog">
                <table
                    id="${dialogId}_LayoutTable"
                    class="ezClockerDialogMessageCell">
                    <tr>
                        ${dialogImage}
                        <td
                            class="ezClockerDialogMessageCell"
                            id="${dialogId}_DialogMessageContainer">
                            <div
                                id="${dialogId}_DialogMessage">
                                ${EzString.stringOrEmpty(dialogContent)}
                            </div>
                        </td>
                    </tr>
                </table>
            </div>`;
    }

    /**
     * @public
     * @function
     * Resolves the result for the dialog associated with the provided dialogId
     * @param {string} dialogId
     * @param {function} resolveCallback
     * @param {undefined|null|*} dialogResult
     * @returns {*}
     * Returns the result of calling the resolveCallback function.
     */
    ezResolveDialogResult(dialogId, resolveCallback, dialogResult) {
        if (!EzString.hasLength(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezResolveDialogResult);
        }
        if (!EzUx.elementExists(dialogId)) {
            throw new EzException(
                `Unable to locate the dialog's root HTML element for dialogId=${dialogId} in the current document.`);
        }
        if (!EzFunction.isFunction(resolveCallback)) {
            throw new EzBadParamException(
                'resolveCallback',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezResolveDialogResult);
        }

        EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

        return resolveCallback(dialogResult);
    }

    /**
     * TODO: Remove this method once all use is migrated
     * @public
     * @function
     * Initializes a default dialog with an OK button.
     * @param {string} dialogId
     * @returns {object}
     * Returns a reference to the element with the provided dialogId
     */
    ezInitDefaultDialog(dialogId) {
        if (!EzString.hasLength(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezInitDefaultDialog);
        }
        if (!EzUx.elementExists(dialogId)) {
            throw new EzException(
                `Unable to locate the dialog's root HTML element for dialogId=${dialogId} in the current document.`);
        }

        const ezDialogConfig = new EzDialogConfig(dialogId);

        ezDialogConfig.buttons = [
            {
                autoOpen: false,
                text: 'OK',
                id: `${dialogId}_OK`,
                click: () => EzDialog.ezInstance.ezResolveDialogResult(
                    dialogId,
                    null,
                    {
                        id: dialogId,
                        dialogStatus: new EzDialogResponse(EzDialogResponseStatus.OK).dialogStatus
                    })
            }
        ];

        return EzUx.$id(dialogId).dialog(ezDialogConfig);
    }

    /**
     * TODO: Remove this method once all use is migrated
     * @public
     * @function
     * Initializes a jQuery dialog
     * @param {string} dialogId
     * @param {undefined|null|array} dialogButtons
     * @param {undefined|null|object} additionalOptions
     * @returns {Object}
     * Returns the reference to the jQuery dialog.
     * @deprecated
     * Will remove in a future release.
     * Migrate to: globalThis.ezApi.ezclocker.ezDialog.ezInitDialogWithConfig(dialogId, ezDialogConfig)
     */
    ezInitDialog(dialogId, dialogButtons, additionalOptions) {
        if (!EzString.hasLength(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezInitDialog);
        }
        if (!EzUx.elementExists(dialogId)) {
            throw new EzException(
                `Unable to locate the dialog's root HTML element for dialogId=${dialogId} in the current document.`);
        }

        if (!EzArray.hasLength(dialogButtons)) {
            EzDialog.ezInstance.ezInitDefaultDialog(dialogId);
        } else {
            const ezDialogConfig = new EzDialogConfig(dialogId);

            ezDialogConfig.buttons = dialogButtons;

            EzUx.$id(dialogId).dialog(ezDialogConfig);

            EzUx.$id(dialogId);
        }

        return EzObject.isValid(additionalOptions)
            ? EzUx.$id(dialogId).dialog(additionalOptions)
            : EzUx.$id(dialogId);
    }

    /**
     * @static
     * @public
     * @function
     * Initializes a jQuery UI Dialog using the div associated with the provided dialogId and the configuration JSON object
     * provided as ezDialogConfig.
     * If the dialogId is null or empty then a id is generated in the format of: EzDialog_{Now_UTC_UNIX_MS_TIME_STAMP}
     * If the ezDialogConfig is undefined or null then a new instance of EzDialogConfig() is used.
     * @param {undefined|null|string} dialogId
     * Default is: `EzDialog_${globalThis.ezApi.ezclocker.ezDateTime.ezGetNowUTCUnixMSTimeStamp()}`
     * @param {undefined|null|EzDialogConfig} ezDialogConfig
     * Default is: new EzDialogConfig(dialogId);
     * @returns {object}
     * Returns the reference to the jQuery UI dialog initialized.
     */
    static ezInitDialogWithConfig(dialogId, ezDialogConfig) {
        if (!EzString.hasLength(dialogId)) {
            throw new EzBadParamException(
                { dialogId },
                EzDialog,
                EzDialog.ezInitDialogWithConfig);
        }
        if ('EzDialogConfig' !== ezDialogConfig.constructor.name) {
            throw new EzBadParamException(
                'dialogId (class EzDialogConfig)',
                EzDialog.ezInstance,
                EzDialog.ezInitDialogWithConfig);
        }
        if (!EzUx.elementExists(dialogId)) {
            throw new EzException(
                `Unable to locate the dialog's root HTML element for dialogId=${dialogId} in the current document.`);
        }

        return EzUx.$id(dialogId).dialog(ezDialogConfig);
    }

    /**
     * TODO: Remove once all use of non-static method EzDailog.ezInitDialogWithConfig(..) is migrated to static method EzDialog.ezInitDialogWithConfig(..)
     * @public
     * @function
     * Initializes a jQuery UI Dialog using the div associated with the provided dialogId and the configuration JSON object
     * provided as ezDialogConfig.
     * If the dialogId is null or empty then a id is generated in the format of: EzDialog_{Now_UTC_UNIX_MS_TIME_STAMP}
     * If the ezDialogConfig is undefined or null then a new instance of EzDialogConfig() is used.
     * @param {undefined|null|string} dialogId
     * Default is: `EzDialog_${globalThis.ezApi.ezclocker.ezDateTime.ezGetNowUTCUnixMSTimeStamp()}`
     * @param {undefined|null|EzDialogConfig} ezDialogConfig
     * Default is: new EzDialogConfig(dialogId);
     * @returns {object}
     * Returns the reference to the jQuery UI dialog initialized.
     * @deprecated
     * Migreate to static method: EzDialog.ezInitDialogWithConfig
     */
    ezInitDialogWithConfig = EzDialog.ezInitDialogWithConfig;

    /**
     * @public
     * @function
     * Shows the dialog associated with the provided id. Dialog needs to have been configured already!
     * @param {string} dialogId
     * @param {undefined|null|boolean} forceStopWaitSpinner
     * Default: false
     * @returns {Promise.resolve}
     */
    ezShowDialog(dialogId, forceStopWaitSpinner) {
        if (!EzString.hasLength(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezShowDialog);
        }
        if (!EzUx.elementExists(dialogId)) {
            throw new EzException(
                `Unable to locate the dialog's root HTML element for dialogId=${dialogId} in the current document.`);
        }

        if (EzBoolean.isTrue(forceStopWaitSpinner)) {
            globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();
        }

        return EzPromise.asyncAction(
            (finished) => {
                EzUx.$id(dialogId).dialog('open');

                return finished();
            });
    }

    /**
     * @public
     * @function
     * Shows the dialog associated with the provided id. Dialog needs to have been configured already!
     * @param {string} dialogId
     * @param {String|null} onShowEventName
     * @param {Boolean|null} forceStopPageWait
     * @returns {Promise.resolve}
     */
    ezShowDialogWithShowEvent(dialogId, onShowEventName, forceStopPageWait) {
        if (!EzString.hasLength(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezShowDialogWithShowEvent);
        }

        if (!EzUx.elementExists(dialogId)) {
            throw new EzException(
                `Unable to locate the dialog's root HTML element for dialogId=${dialogId} in the current document.`);
        }

        return EzPromise.asyncAction(
            (finished) => {
                const response = EzDialog.ezInstance.ezBuildBasicSuccessResponse();

                if (EzBoolean.isTrue(forceStopPageWait)) {
                    globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();
                }

                EzUx.$id(dialogId).dialog('open');

                if (EzString.hasLength(onShowEventName) && globalThis.ezApi.ezclocker.ezEventEngine.ezIsEventRegistered(onShowEventName)) {
                    globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                        onShowEventName,
                        globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                            EzDialog.ezApiName,
                            `Dialog with dialogId=${dialogId} is open and visible.`,
                            {
                                dialogId
                            }
                        ));
                }

                return finished(response);
            });
    }

    /**
     * @public
     * @function
     * Closes the visible jquery dialog associatd with the provided id. Optionally triggers the provided event name
     * after the dialog is closed.
     * @param {string} dialogId
     * @param {string|null} onCloseEventName
     */
    ezCloseDialog(dialogId, onCloseEventName) {
        if (!EzString.hasText(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezShowDialogWithShowEvent);
        }
        if (!EzUx.elementExists(dialogId)) {
            throw new EzException(
                `Unable to locate the dialog's root HTML element for dialogId=${dialogId} in the current document.`);
        }

        try {
            EzUx.$id(dialogId).dialog('close');

            if (EzString.hasText(onCloseEventName) && globalThis.ezApi.ezclocker.ezEventEngine.ezIsEventRegistered(onCloseEventName)) {
                globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                    onCloseEventName,
                    globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                        dialogId,
                        `Dialog with dialogId=${dialogId} closed.`,
                        {
                            dialogId
                        }
                    ));
            }
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(`Failed to close dialog with dialogId=${dialogId} due to error: ${err.message}`);
        }
    }

    /**
     * @public
     * @function
     * Sets options for a jquery dialog associated with the provided dialogId
     * @param {string} dialogId
     * @param {undefined|null|string} title
     * Default: 'Confirm'
     * @param {undefined|null|string} message
     * Default: 'Everyone at ezClocker hopes you have an AWESOME day!'
     * @param {undefined|null|number} optionalWidth
     * @param {undefined|null|number} optionalHeight
     */
    ezSetDialogOptions(dialogId, title, message, optionalWidth, optionalHeight) {
        if (!EzString.hasLength(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezShowDialogWithShowEvent);
        }
        if (!EzUx.elementExists(dialogId)) {
            throw new EzException(
                `Unable to locate the dialog's root HTML element for dialogId=${dialogId} in the current document.`);
        }

        const ezDialogConfig = new EzDialogConfig(dialogId);

        if (EzNumber.isNumber(optionalWidth)) {
            ezDialogConfig.width = optionalWidth;
        }

        if (EzNumber.isNumber(optionalHeight)) {
            ezDialogConfig.height = optionalHeight;
        }

        ezDialogConfig.title = EzString.stringOrDefault(
            title,
            'Confirm');

        EzUx.$id(dialogId).dialog({
            title: ezDialogConfig.title,
            height: ezDialogConfig.height,
            width: ezDialogConfig.width
        });

        const dialogMessageId = `${dialogId}_DialogMessage`;

        if (EzUx.elementExists(dialogMessageId)) {
            globalThis.ezApi.ezclocker.ezUi.ezSetContent(
                dialogMessageId,
                EzString.stringOrDefault(
                    message,
                    'Everyone at ezClocker hopes you have an AWESOME day!'));
        }
    }

    /**
     * @public
     * @function
     * Builds an ezClocker API error response object.
     * @param {undefined|null|number} errorCode
     * Default: EzErrorCode.INTERNAL
     * @param {undefined|null|string} errorMessage
     * Default is: `An unexpected error occured. ${EzDialog.ezInstance.TRY_AGAIN_THEN_REPORT}`
     * @returns {object}
     */
    ezBuildErrorResponse(errorCode, errorMessage) {
        return {
            errorCode: EzNumber.isNumber(errorCode)
                ? errorCode
                : EzErrorCode.INTERNAL,
            message: EzString.hasLength(errorMessage)
                ? errorMessage
                : `An unexpected error occured. ${EzDialog.ezInstance.TRY_AGAIN_THEN_REPORT}`
        };
    }

    /**
     * @public
     * @function
     * Builds a basic success response
     * @param {undefined|null|string} message
     * Default: 'Success'
     * @returns {object}
     * Basic success API response object in the format:
     * {
     *     errorCode: 0,
     *     message: {message|'Success'}
     * }
     */
    ezBuildBasicSuccessResponse(message) {
        return {
            errorCode: 0,
            message: EzString.hasLength(message)
                ? message
                : 'Success'
        };
    }

    /**
     * @public
     * @function
     * Shows the authentication session expired or no longer valid message.
     * @returns {Promise.resolve}
     */
    ezShowSessionExpiredDialog() {
        globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

        return EzDialog.ezInstance.ezShowErrorDialog(
            // eTitle
            'Authentication Session Expired',
            // eMessage
            'Your authentication session is expired or no longer valid. Please sign in.',
            // details
            null,
            // optionalData
            null,
            // optionalWidth
            null,
            // optionalHeight
            null);
    }

    /**
     * @public
     * @function
     * Shows the QBO  session expired or no longer valid message.
     * @returns {Promise.resolve}
     */
    ezShowQBOSessionExpiredDialog() {
        globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

        return EzDialog.ezInstance.ezShowErrorDialog(
            // eTitle
            'Quickbooks Session Expired',
            // eMessage
            'You need to login to Quickbooks Online.',
            // details
            null,
            // optionalData
            null,
            // optionalWidth
            null,
            // optionalHeight
            null);
    }

    /**
     * @public
     * @function
     * Generates an error message from the provided responseMessage and errorCode
     * @param {undefined|null|string} responseMessage
     * Default: EzString.EMPTY
     * @param {undefined|null|number} errorCode
     * Default: null
     * @returns {string}
     */
    ezGenerateErrorMessage(responseMessage, errorCode) {
        const em = EzString.hasLength(responseMessage)
            ? `Error reported: ${responseMessage}<br/>${EzDialog.ezInstance.TRY_AGAIN_THEN_REPORT}`
            : EzDialog.ezInstance.TRY_AGAIN_THEN_REPORT;

        return EzNumber.isNumber(errorCode)
            ? EzHtml.build`
                <div>
                    ${em}
                <div>
                <i>
                    Error Code: ${errorCode}
                </i>`
            : em;
    }

    /**
     * @public
     * @function
     * Evaluates an ezClocker service response. If the response is not valid, or has an errorCode
     * value that is not zero, the response is considered an error response.
     * Returns a promise, resolving if the response is an error response. Rejecting if the service
     * is not an error response.
     * @param {object} response
     * @param {string|null} errorTitle
     * @param {string|null} errorMessage
     * @param {boolean|null} showUi
     * @returns {Promise.resolve}
     */
    ezIsServiceResponseError(response, errorTitle, errorMessage, showUi) {
        return globalThis.ezApi.ezPromise(
            (resolve, reject) => !EzObject.isValid(response) || EzObject.isValid(response) && EzObject.isNumber(response.errorCode) && 0 !== response.errorCode
                ? EzDialog.ezInstance.ezHandleSpecialErrorCodes(
                    response.errorCode,
                    EzString.stringOrDefault(errorTitle, 'EzClocker Error'),
                    `${EzString.stringOrEmpty(errorMessage)} ${EzDialog.ezInstance.ezGenerateErrorMessage(response.message)}`,
                    EzBoolean.isTrue(showUi))
                    .then(
                        () => resolve(response))
                : reject(response));
    }

    /**
     * Handles special error codes that have defined actions. If none match, then the error is just logged.
     * Optionally, an error message UI can pop-up if showUi = true
     * @public
     * @function
     * @param {string|null} errorTitle
     * @param {string|null} errorMessage
     * @param {boolean|null} showUi
     * @returns {Promise}
     */
    ezHandleSpecialErrorCodes(errorCode, errorTitle, errorMessage, showUi) {
        if (!EzNumber.isNumber(errorCode)) {
            errorCode = 0;
        }

        if (EzBoolean.ezIsTrue(showUi)) {
            globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();
        }

        return EzPromise.asyncAction(
            (finished) => {
                switch (errorCode) {
                    case 401:
                        globalThis.ezApi.ezclocker.ezLogger.error('User is not authenticated (redirected to sign out).');

                        globalThis.ezApi.ezclocker.ezNavigation.ezSignOut();

                        break;
                    case 403:
                        globalThis.ezApi.ezclocker.ezLogger.error('License needs attention (redirected to account page).');

                        globalThis.ezApi.ezclocker.ezNavigation.ezNavigateToEmployerAccountPage(
                            `employer=${globalThis.ezApi.ezclocker.ezEmployerDashboardController.activeEmployerId}&expired=1`);

                        break;
                    case 409:
                        // Normally user exists error, let UX handle it
                        break;
                    default:
                        if (EzString.hasLength(errorMessage)) {
                            globalThis.ezApi.ezclocker.ezLogger.error(errorMessage);

                            if (EzBoolean.isTrue(showUi)) {
                                return EzDialog.ezInstance.ezShowError(
                                    errorTitle,
                                    errorMessage,
                                    finished)
                                    .then(finished);
                            }
                        }
                }

                return finished();
            });
    }

    /**
     * @public
     * @function
     * Generates a basic error message for service call failures.
     * @param {undefined|null|string} errorTitle
     * Default: 'EzClocker Error'
     * @param {undefined|null|object} eResponse
     * Default: null
     * @param {undefined|null|string} defaultErrorMessage
     * Default: 'EzClocker encountered an error while performing a task. No additional details were provided.'
     * @param {undefined|null|boolean} showUi
     * Default: true
     * @returns {Promise.resolve}
     */
    ezShowNon200ServiceError(errorTitle, eResponse, defaultErrorMessage, showUi) {
        if (!EzBoolean.isBoolean(showUi)) {
            showUi = true;
        }

        if (EzBoolean.isTrue(showUi)) {
            globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();
        }

        return EzPromise.asyncAction(
            (finished) => {
                if (!EzString.hasLength(errorTitle)) {
                    errorTitle = 'EzClocker Error';
                }

                if (!EzString.hasLength(defaultErrorMessage)) {
                    defaultErrorMessage = 'EzClocker encountered an error while performing a task. No additional details were provided.';
                }

                const errorCode = EzObject.isValid(eResponse) && EzNumber.isNumber(eResponse.errorCode)
                    ? eResponse.errorCode
                    : EzErrorCode.INTERNAL;

                const em = EzDialog.ezInstance.ezGenerateErrorMessage(
                    EzString.stringOrDefault(eResponse.message, defaultErrorMessage),
                    errorCode);

                globalThis.ezApi.ezclocker.ezLogger.error(`${em} Error response: ${EzJson.toJson(eResponse)}`);

                return EzBoolean.isTrue(showUi)
                    ? EzDialog.ezInstance.ezShowError(errorTitle, em, finished)
                    : finished(eResponse, errorTitle, em);
            });
    }

    /**
     * @public
     * @function
     * Returns the message in the response, or blank string if none.
     * @param {undefined|null|object} eResponse
     * @returns {string}
     */
    ezExtractErrorMessageFromResponse(eResponse) {
        return EzObject.isValid(eResponse) && EzString.hasLength(eResponse.message)
            ? eResponse.message
            : EzString.EMPTY;
    }

    /**
     * @public
     * @function
     * Returns the message in the response, or blank string if none.
     * @param {undefined|null|number} errorCode
     * @param {undefined|null|string} errorMessage
     * @param {undefined|null|string} additionalDetails
     * @param {undefined|null|string} serviceDetails
     * @returns {string}
     */
    ezCreateDefectDetails(errorCode, errorMessage, additionalDetails, serviceDetails) {
        return EzHtml.build`
            <div>
                <textarea
                    class="ez-error-message-details"
                    style="height:200px;">
                    ******************************\n
                    : Error Details              :\n
                    ******************************\n
                    Error Code: ${EzNumber.numberOrDefault(errorCode, EzErrorCode.INTERNAL)}\n\n
                    ------------------------------\n
                    : Error                      :\n
                    ------------------------------\n
                    ${EzString.stringOrDefault(errorMessage, 'N/A')}\n\n
                    ------------------------------\n
                    : Additional Details         :\n
                    ------------------------------\n
                    ${EzString.stringOrDefault(additionalDetails, 'N/A')}\n\n
                    ------------------------------\n
                    : Service Details            :\n
                    ------------------------------\n
                    ${EzString.stringOrDefault(serviceDetails, 'N/A')}
                </textarea>
            </div>`;
    }

    /**
     * TODO: Remove method once all use is migrated
     * @public
     * @function
     * Dialog for reporting internally detected errors
     * @param {object} jqXHR
     * @param {string} error
     * @param {string} errorMessage
     * @param {string} additionalDetails
     * @param {string} action
     * @param {boolean}
     * @deprecated
     * Migrate to:
     *  globalThis.ezApi.ezclocker.ezDialog.ezShowPossibleDefectError(
     *      error,
     *      errorMessage = EzString.EMPTY,
     *      _jqXHR = null,
     *      additionalDetails = EzString.EMPTY,
     *      serviceDetails = EzString.EMPTY)
     */
    ezReportInternalBugDialog(_jqXHR, error, errorMessage, additionalDetails, action, refreshPage = false) {
        const emDetails = !EzString.isString(additionalDetails)
            ? ` ${EzJson.toJson(additionalDetails)}`
            : ` ${EzString.stringOrEmpty(additionalDetails)}`;

        const errorCode = EzObject.isValid(_jqXHR)
            ? EzObject.assignOrDefault(
                _jqXHR?.statusCode,
                EzErrorCode.INTERNAL.toString())
            : EzErrorCode.INTERNAL.toString();

        const serviceDetails = EzObject.isValid(_jqXHR)
            ? EzJson.toJson(_jqXHR)
            : 500;

        const closeHandler = EzFunction.isFunction(action)
            ? () => {
                action();

                if (EzBoolean.booleanOrFalse(refreshPage)) {
                    // refresh the browser to reset the page;
                    window.location.reload();
                }
            }
            : () => {
                if (EzBoolean.booleanOrFalse(refreshPage)) {
                    // refresh the browser to reset the page;
                    window.location.reload();
                }
            };

        globalThis.ezApi.ezclocker.ezLogger.error(`[Error #${errorCode}] ${errorMessage} [Error details: ${emDetails}]`);

        // Send error report via email
        EzDialog.ezInstance.ezSendErrorReport(
            EzDialog.ezInstance.ezCreateDefectDetails(
                errorCode,
                errorMessage,
                emDetails,
                serviceDetails))
            .then(
                EzPromise.ignoreResolve,
                (eResponse) => {
                    globalThis.ezApi.ezclocker.ezLogger.error(
                        EzString.em`
                            Failed to send error report for error: ${errorMessage}.
                            [Error: ${EzJson.toJson(eResponse)}]`);
                });

        // title, message, onCloseCallback, passThroughData, width, height
        return EzDialog.ezInstance.ezShowOK(
            error,
            EzHtml.build`
                EzClocker encounted an unexpected error during the last operation.
                You my retry the operation again, however, if you continue to receive this error
                please contact our support team at
                <a
                    href="mailto:support@ezclocker.com?subject=\'Receiving error: ${errorMessage}\'">
                    support@ezclocker.com
                </a>
                for assistance.
                <p
                    id="EzAppology">
                    We appologize for the disruption to your work.
                </p>
                <div
                    id="EzTechnicalErrorDetailsContainer"
                    class="ez-error-message-textarea-container">
                    <label
                        id="EzTechnicalDetailsTextAreaLabel"
                        for="EzTechnicalDetailsTextArea"
                        class="ez-faded-error-details">Technical Details</label>
                    <div>
                        <textarea id="EzTechnicalDetailsTextArea" class="ez-error-message-details" rows="4">
                        ${emDetails.trim()}
                    </textarea>
                    </div>
                </div>`,
            null,
            EzNumber._700)
            .then(closeHandler);
    }

    /**
     * @public
     * @function
     * Dialog for reporting internally detected errors
     * @param {string} error
     * @param {string} errorMessage
     * Default: EzString.EMPTY
     * @param {undefined|null|object} _jqXHR (no longer used)
     * Default: null
     * @param {undefined|null|string} additionalDetails
     * Default: EzString.EMPTY
     * @param {undefined|null|string} serviceDetails
     * Default: EzString.EMPTY
     * @returns {Promise.resolve}
     */
    ezShowPossibleDefectError(error, errorMessage = EzString.EMPTY, _jqXHR = null, additionalDetails = EzString.EMPTY, serviceDetails = EzString.EMPTY) {
        globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

        globalThis.ezApi.ezclocker.ezLogger.debug(`jqXHR provided: ${EzObject.isValid(_jqXHR)}`);

        let errorDetails = EzString.hasLength(errorMessage)
            ? EzHtml.build`
                <div
                    id="EzPossibleDefect_ErrorMessageContainer">
                    <p
                        id="EzPossibleDefect_ErrorMessage">
                        ${errorMessage}
                    </p>
                </div>`
            : EzString.EMPTY;

        if (EzObject.isValid(additionalDetails)) {
            const additionalSupportDetailsMessage = EzString.isString(additionalDetails)
                ? EzString.em`
                    ${errorDetails}
                    ${EzString.stringOrDefault(additionalDetails, 'No additional details provided.')}`
                : EzString.em`
                    ${errorDetails}
                    ${EzJson.toJson(EzObject.assignOrDefault(additionalDetails, { message: 'No additional details provided.' }))}`;

            errorDetails = EzHtml.build`
                ${errorDetails}
                <div
                    id="EzPossibleDefectDialog_AdditionalInformationDiv"
                    class="ez-faded-error-details">
                    <label
                        id="EzPossibleDefect_AdditionalDetails_JsonDataTextView_Label"
                        for="EzPossibleDefect_AdditionalDetails_JsonDataTextView">
                        Additional Information for ezClocker Support:
                    </label>
                    <textarea
                        id="EzPossibleDefect_AdditionalDetails_JsonDataTextArea"
                        class="ez-error-message-details"
                        rows="15">
                        ${additionalSupportDetailsMessage}
                    </textarea>
                </div>`;
        }

        EzDialog.ezInstance.ezSendErrorReport(
            EzDialog.ezInstance.ezCreateDefectDetails(
                error,
                errorMessage,
                EzObject.assignOrDefault(
                    additionalDetails,
                    EzString.EMPTY),
                EzObject.assignOrDefault(
                    serviceDetails,
                    EzString.EMPTY)))
            .then(
                EzPromise.ignoreResolve,
                (eResponse) => {
                    globalThis.ezApi.ezclocker.ezLogger.error(
                        EzString.em`
                            Failed to send error report for error: ${errorMessage}.
                            [Error: ${EzJson.toJson(eResponse)}]`);
                });

        const emailBody = `I am receiving error "${error}: ${errorMessage}" when using ezClocker. Can you please help me resolve this problem?`;

        globalThis.ezApi.ezclocker.ezLogger.error(`${error}: ${errorMessage} [Details: ${errorDetails}]`);

        if (EzString.hasText(error)) {
            const mailUrl = `mailto:support@ezclocker.com?subject='Help with ${error}'&body='${emailBody}'`;

            const message = EzHtml.build`
                <p
                    id="EzPossibleDefect_ErrorMessage1">
                    EzClocker was unable to communicate to our cloud servers. You may retry the operation after a short delay.
                </p>
                <p
                    id="EzPossibleDefect_ErrorMessage2">
                    If you continue to experience problems or need immediate help please contact our support team at
                    <a
                        id="EzEmailSupportLink"
                        href="${mailUrl}">
                        support@ezclocker.com
                    </a>.
                </p>
                Thank you for your patience!
                <div
                    id="EzErrorDetailsContainer"
                    class="ez-error-details-container">
                    ${errorDetails}
                </div>`;

            return EzDialog.ezInstance.ezShowOK(
                // title
                error,
                // message
                message,
                // passThroughData
                null,
                // width
                EzNumber._800,
                // height
                EzNumber._600);
        }

        return EzPromise.resolve();
    }

    /**
     * @public
     * @function
     * Sends an unhandled exception to exceptions@ezclocker.com
     * @param {string} errorReportDetails
     * @returns {Promise}
     */
    ezSendErrorReport(errorReportDetails) {
        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
            globalThis.ezApi.ezclocker.ezNavigation.getPublicApiUrl('email/send-error-report'),
            EzJson.toJson({
                userId: null,
                errorDetails: errorReportDetails
            }))
            .then(
                EzPromise.ignoreResolve,
                EzPromise.ignoreReject);
    }

    /**
     * @public
     * @function
     * Creates support contact information HTML message
     * @param {string} emailTitle
     * Default: 'Receiving Error Messages from ezClocker Website'
     * @param {string} emailBody
     * Default: EzString.EMPTY
     * @returns {string}
     */
    ezCreateSupportContactInfo(
        emailTitle = 'Receiving Error Messages from ezClocker Website',
        emailBody = EzString.EMPTY) {

        if (!EzString.isString(emailBody)) {
            const errorMessage = EzObject.isValid(emailBody)
                ? EzJson.toJson(emailBody)
                : '[please enter the error you recenved here]';

            emailBody = EzString.msg`
                I received the following error when using the ezClocker website:

                ${EzString.stringOrDefault(errorMessage, '[please enter the error you recenved here]')}

                Can you please help me resolve this?`;
        }

        return EzHtml.build`
            <p>
                Please try refreshing the page and trying the operation again. If the problem continues then please contact
                ezClocker at
                <a
                    href="mailto:support@ezclocker.com?subject=${emailTitle}&body=${emailBody}">
                    support@ezclocker.com
                </a>
                for assistence.
            </p>
            <p>
                When contacting support please include your username for the account, any error messages you saw,
                and what you were attempting to do where the problem occurred. The more details our support has the faster they can get
                the problem resolved for you.
            </p>
            <p>
                If you would prefer support call you back as soon as possible please include your name, the call back phone number,
                an the best time to contact you.
            </p>
            <h4>
                All of us at ezClocker do appologize for the disruption of your day!
            </h4>`;
    }

    /**
     * @public
     * @function
     * Dialog to inform the user that their session has expired.
     * @param {undefined|null|string|number} statusCode
     */
    ezServiceDownDialog(statusCode) {
        globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

        EzDialog.ezInstance.ezShowOK(
            'EzClocker Connectivity Error',
            EzString.em`
                Error Code: ${EzObject.assignOrDefault(statusCode, EzErrorCode.INTERNAL.toString())}
                <p>
                    EzClocker is having problems connecting to our cloud services at this time.
                    Occasionally the connection problem is due to unexpected maintence work. Please wait a few minuets and then refresh this page.
                </p>
                <p>
                    If you continue to receive this error:
                </p>
                ${EzDialog.ezInstance.ezCreateSupportContactInfo('EzClocker Connectivity Error')}`)
            .then(globalThis.ezApi.ezclocker.ezNavigation.signOut);
    }

    /**
     * @public
     * @function
     * A common dialog to display unexpected service call failure results
     * @param {undefined|null|string} title
     * Default: 'EzClocker Error'
     * @param {undefined|null|string} errorMessage
     * @param {undefined|null|number|string} statusCode
     * Default: EzErrorCode.INTERNAL
     * @param {undefined|null|object} jqXHR
     * Default: {
     *      status: statusCode,
     *      response: errorMessage
     *  }
     * @param {undefined|null|object} eResponse
     * Default: {
     *      errorCode: statusaCode,
     *      message: errorMessage
     *  }
     * @returns {Promise.resolve}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDialog.ezServiceCallFailed(title, errorMessage, statusCode, jqXHR, eResponse)
     */
    ezSomethingIsWrong(title, errorMessage, statusCode, jqXHR, eResponse) {
        if (!EzString.hasLength(errorMessage)) {
            throw new EzBadParamException(
                'errorMessage',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezSomethingIsWrong);
        }

        if (!EzNumber.isNumber(statusCode) && !EzString.hasLength(statusCode)) {
            statusCode = EzErrorCode.INTERNAL;
        }

        if (!EzObject.isValid(eResponse)) {
            eResponse = {
                errorCode: statusCode,
                message: errorMessage
            };
        }

        if (!EzObject.isValid(jqXHR)) {
            jqXHR = {
                status: statusCode,
                response: errorMessage
            };
        }

        globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

        return EzDialog.ezInstance.ezShowOK(
            EzString.stringOrDefault(
                title,
                'EzClocker Error'),
            EzHtml.build`
                <h4>Error</h4>
                <p>
                    ${errorMessage}
                </p>
                <p>
                    Please contact <a href="mailto:support@ezclocker.com">support@ezclocker.com</a> and include the
                    contents of this error message if you have questions or need immediate assistence.
                </p>
                <div
                    id="EzTechnicalDetailsContainer">
                    <label
                        id="EzTechnicalDetailsTextAreaLabel"
                        for="EzTechnicalDetailsTextArea"
                        class="ez-faded-error-details">
                        Error Technical Details
                    </label>
                    <div>
                    <textarea
                        id="EzTechnicalDetailsTextArea"
                            class="ez-faded-error-details"
                            rows="4">
                        Error Code: ${EzObject.assignOrDefault(statusCode, EzErrorCode.INTERNAL.toString())}
                        Service Response: ${EzString.stringOrDefault(EzJson.toJson(eResponse), 'None Available')}
                        Details: ${EzString.stringOrDefault(EzJson.toJson(jqXHR), 'No additional details')}
                    </textarea>
                    </div>
                </div>`,
            null,
            EzDialogSizes.widths.w600);
    }

    /**
     * @public
     * @function
     * A common dialog to display unexpected service call failure results
     * @param {undefined|null|string} title
     * Default: 'EzClocker Error'
     * @param {undefined|null|string} errorMessage
     * @param {undefined|null|string|number} statusCode
     * Default: EzErrorCode.INTERNAL
     * @param {undefined|null|object} jqXHR
     * Default: {
     *      status: statusCode,
     *      response: errorMessage
     *  }
     * @param {undefined|null|object} eResponse
     * Default: {
     *      errorCode: statusaCode,
     *      message: errorMessage
     *  }
     * @returns {Promise.resolve}
     */
    ezServiceCallFailed(title, errorMessage, statusCode, jqXHR, eResponse) {
        if (!EzString.hasLength(errorMessage)) {
            throw new EzBadParamException(
                'errorMessage',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezServiceCallFailed);
        }

        if (!EzNumber.isNumber(statusCode) && !EzString.hasLength(statusCode)) {
            statusCode = EzErrorCode.INTERNAL;
        }

        if (!EzObject.isValid(eResponse)) {
            eResponse = {
                errorCode: statusCode,
                message: errorMessage
            };
        }

        if (!EzObject.isValid(jqXHR)) {
            jqXHR = {
                status: statusCode,
                response: errorMessage
            };
        }

        globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

        return EzDialog.ezInstance.ezShowOK(
            EzString.stringOrDefault(title, 'EzClocker Error'),
            EzHtml.build`
                <h4>Error</h4>
                <p>
                    ${errorMessage}
                </p>
                <p>
                    Please contact <a href="mailto:support@ezclocker.com">support@ezclocker.com</a> and include the
                    contents of this error message if you have questions or need immediate assistence.
                </p>
                <h4>Technical Details</h4>
                <ul>
                    <li>Error Code: ${EzObject.assignOrDefault(statusCode, EzErrorCode.INTERNAL.toString())}</li>
                    <li>Service Response:${EzString.stringOrDefault(EzJson.toJson(eResponse), 'None Available')}</li>
                    <li>Details:${EzString.stringOrDefault(EzJson.toJson(jqXHR), 'No additional details')}</li>
                </ul>`);

    }

    /**
     * @public
     * @function
     * Present dialog for legal blocks, censorship, government mandates
     */
    ezServiceLegalBlockDialog() {
        globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

        EzDialog.ezInstance.ezShowOK(
            // Title
            'Unavailable for Legal Reasons',
            // Message
            EzHtml.build`
                Law enforcement, a government authority, or another entity in which ezClocker is bound to has
                requested that we deny access at this time. We cannot provide any additional information at this time.
                <p>
                    Additional information about the error is availalbe online from Wikipedia:
                    <a
                        id="EzWikipediaResearchLink"
                        target="_research_error"
                        href="https://en.wikipedia.org/wiki/HTTP_451">
                        Read about the Unavaialble for Legal Reasons Error on Wikipedia
                    </a>
                </p>`)
            .then(globalThis.ezApi.ezclocker.ezNavigation.signOut);
    }

    /**
     * @public
     * @function
     * Shows a yes/no question dialog.
     * @param {undefined|null|string} title
     * Default: 'EzClocker Confirmation'
     * @param {undefined|null|string} message
     * Default: EzString.EMPTY
     * @param {undefined|null|function} onCloseCallback
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default is: Default value for property width on EzDialogConfig
     * @param {undefined|null|number} optionalHeight
     * Default is: Default value for property height on EzDialogConfig
     * @param {undefined|null|boolean} useOldResponse
     * @returns {Promise.resolve}
     */
    ezYesNoDialog(title, message, onCloseCallback, optionalWidth, optionalHeight, useOldResponse) {
        return EzPromise.asyncAction(
            (finished) => {
                const dialogId = 'EzYesNoDialog';

                EzBoolean.isTrue(useOldResponse);

                const handleEzYesNoDialogResponse = (response) => {
                    EzDialog.ezInstance.ezCloseDialog(dialogId);

                    globalThis.ezApi.ezOnCallback(onCloseCallback, response);

                    return finished(response);
                };

                // Inject the dialog with the specified id
                EzUx.appendContent(
                    'body',
                    EzDialog.ezInstance.ezBuildDialogFromTemplate(
                        dialogId,
                        globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicImagesUrl('icons/question-navy.svg'),
                        message));

                const ezDialogConfig = new EzDialogConfig(dialogId);

                ezDialogConfig.title = EzString.stringOrDefault(title, 'EzClocker Confirmation');

                ezDialogConfig.ezRemoveOnClose = true;

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_YesButton`,
                        text: 'Yes',
                        click: () => handleEzYesNoDialogResponse(
                            EzBoolean.isTrue(useOldResponse)
                                ? 'YES'
                                : EzDialogResponse.YES)
                    },
                    {
                        id: `${dialogId}_NoButton`,
                        text: 'No',
                        click: () => handleEzYesNoDialogResponse(
                            EzBoolean.isTrue(useOldResponse)
                                ? 'NO'
                                : EzDialogResponse.NO)
                    }
                ];

                document.querySelector('[aria-label="Message Body"]');

                ezDialogConfig.close = () => EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzNumber.isNumber(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.ezCloseButtonHandler = () => handleEzYesNoDialogResponse(
                    EzBoolean.isTrue(useOldResponse)
                        ? 'NO'
                        : EzDialogResponse.NO);

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    dialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Sets a Jquery dialog's title bar close button click event.
     * The button is located by the aria-describedby attribute and is assumed to
     * equal the dialogId it applies to.
     * @param {string} dialogId
     * @param {function} onClickHandler
     */
    ezSetDialogTitleCloseButtonClickHandler(dialogId, onClickHandler) {
        if (!EzString.hasLength(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezSetDialogTitleCloseButtonClickHandler);
        }
        if (!EzFunction.isFunction(onClickHandler)) {
            throw new EzBadParamException(
                'onClickHandler',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezSetDialogTitleCloseButtonClickHandler);
        }
        if (!EzUx.elementExists(dialogId)) {
            throw new EzException(`Unable to locate the dialog HTML element with id=${dialogId}.`);
        }

        // Hook the dialog's small close button click event (in the title)
        const elements = globalThis.ezApi.ezclocker.ezUi.ezFindByAriaAttribute('describedby', dialogId);

        if (EzArray.arrayHasLength(elements)) {
            for (const element of elements) {
                element.click = onClickHandler;
            }
        }
    }

    /**
     * @public
     * @function
     * Shows a yes/no question dialog.
     * @param {undefined|null|string} title
     * @param {undefined|null|string} message
     * @param {undefined|null|*} passThroughData
     * @param {undefined|null|number} optionalWidth
     * @param {undefined|null|number} optionalHeight
     * @param {undefined|null|string} optionalDialogId
     * @param {undefined|null|function} onBeforeClose
     * @param {undefined|null|string} optionalIconImageUrl
     * @param {undefined|null|string} primaryButtonText
     * Default: 'Yes'
     * @param {undefined|null|string} secondaryButtonText
     * Default: 'No'
     * @returns {Promise.resolve}
     */
    ezShowYesNo(title, message, passThroughData, optionalWidth, optionalHeight, optionalDialogId, onBeforeClose, optionalIconImageUrl,
        primaryButtonText = 'Yes', secondaryButtonText = 'No') {
        return EzPromise.asyncAction(
            (finished) => {
                const dialogId = EzString.stringOrDefault(
                    optionalDialogId,
                    `EzYesNoDialog_${globalThis.ezApi.ezclocker.ezDateTime.ezNowAsTimestamp()}`);

                const iconImageUrl = EzString.stringOrDefault(
                    optionalIconImageUrl,
                    globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicImagesUrl('icons/info.svg'));

                // Inject the dialog with the specified id
                EzUx.appendContent(
                    'body',
                    EzDialog.ezInstance.ezBuildDialogFromTemplate(
                        dialogId,
                        iconImageUrl));

                globalThis.ezApi.ezclocker.ezUi.ezSetContent(
                    `${dialogId}_DialogMessage`,
                    EzString.stringOrEmpty(message));

                const ezDialogConfig = new EzDialogConfig(dialogId);

                ezDialogConfig.title = EzString.stringOrDefault(title, 'EzClocker Confirmation');

                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzNumber.isNumber(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_YesButton`,
                        text: primaryButtonText,
                        click: () => {
                            if (EzFunction.isFunction(onBeforeClose)) {
                                onBeforeClose();
                            }

                            return EzDialog.ezInstance.ezResolveDialogResult(
                                dialogId,
                                finished,
                                {
                                    id: dialogId,
                                    dialogStatus: EzDialogResponse.YES.dialogStatus,
                                    passThroughData
                                });
                        }
                    },
                    {
                        id: `${dialogId}_NoButton`,
                        text: secondaryButtonText,
                        click: () => {
                            if (EzFunction.isFunction(onBeforeClose)) {
                                onBeforeClose();
                            }

                            return EzDialog.ezInstance.ezResolveDialogResult(
                                dialogId,
                                finished,
                                {
                                    id: dialogId,
                                    dialogStatus: EzDialogResponse.NO.dialogStatus,
                                    passThroughData
                                });
                        }
                    }
                ];

                ezDialogConfig.close = () => EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

                EzUx.$id(dialogId).dialog(ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * @param {undefined|null|string} title
     * Default: 'EzClocker Error'
     * @param {undefined|null|string} message
     * Default: EzString.EMPTY
     * @param {undefined|null|function} onCloseCallback
     * Default: null
     * @param {undefined|null|*} passThroughData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default is: Default value for property width on EzDialogConfig
     * @param {undefined|null|number} optionalHeight
     * Default is: Default value for property height on EzDialogConfig
     * @returns {Promise.resolve}
     */
    ezShowError(title, message, onCloseCallback, passThroughData, optionalWidth, optionalHeight) {
        return EzPromise.asyncAction(
            (finished) => {
                const dialogId = 'EzErrorDialog';

                if (EzUx.elementExists(dialogId)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);
                }

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <div
                            id="${dialogId}"
                            class="ezDialog-container"
                            title="${EzString.stringOrDefault(title, 'EzClocker Error')}">
                            <div
                                id="${dialogId}_LayoutTable"
                                class="ezDialog-layout-container">
                                <div
                                    id="${dialogId}_IconParagraph"
                                    class="ezGrid-align-left ezGrid-vertical-align-top ezPad8">
                                    <img
                                        id="${dialogId}_IconImage"
                                        class="ezDialog-icon"
                                        src="${globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicImagesUrl('dialog_error_32x32.png')}"/>
                                </div>
                                <div
                                    id="${dialogId}_MessageContainer"
                                    class="ezGrid-align-left ezGrid-vertical-align-top ezPad8 ezDialog-message-container">
                                    ${EzString.stringOrEmpty(message)}
                                </div>
                            </div>
                        </div>`);

                const ezDialogConfig = new EzDialogConfig(dialogId);

                ezDialogConfig.title = `${EzString.stringOrDefault(title, 'EzClocker Error')}`;

                if (EzObject.isValid(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzObject.isValid(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_OkButton`,
                        text: 'OK',
                        click: () => EzDialog.ezInstance.ezCloseDialog(dialogId)
                    }
                ];

                ezDialogConfig.close = (...args) => {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

                    globalThis.ezApi.ezCallback(
                        onCloseCallback,
                        EzDialogResponse.OK,
                        passThroughData,
                        args);

                    return finished(EzDialogResponse.OK, passThroughData);
                };

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    dialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Builds a funny generic error message to help users report the problem. Can optionally include
     * stack trace details if includeStackTraceDetails param is true.
     * @param {boolean} includeStackTraceDetails
     * @returns {string}
     */
    ezBuildUnhandledErrorMessage(includeStackTraceDetails) {
        let em = EzHtml.build`
            EzClocker has encountered an error that the development team did not handle properly (shame on them!).
            If you wish, you can report this error via email to
            <a
                href="mailto://support@ezclocker.com">
                support@ezclocker.com
            </a>
            with the subject line "Attention: ezClocker Leadership" and a leadership team member will
            personally make sure our development team addresses the problem (even if it means they will need to
            work through the night)!
            <p>
                All joking aside, we love when customers report errors they encounter as it greatly helps us quickly
                correct errors, bugs and mistakes. Just send a quick email to
                <a
                    href="mailto://support@ezclocker.com>
                    support@ezclocker.com
                </a>
                and mention what you were attempting to do. Include the error message and any additional details
                reported in the error message. You can also attach any screen shot images or other data you feel might
                help the poor development team identify, reproduce, and fix the problem.
            </p>
            <p>
                Every single team member at ezClocker appologizes for the interruption of your work due to
                the error. We know you have many choices in software for your busness and we feel honored that you
                choose to use ezClocker for your business.
            <p>
                All of the ezClocker teams have a #1 goal of providing a positive and rewarding experience to our
                customers and work hard to to make sure any negative or bad experience is corrected as fast as possible.
            </p>`;

        if (EzBoolean.isTrue(includeStackTraceDetails)) {
            em = EzHtml.build`
                ${em}
                <div class="ez-faded-error-details">
                    <p>Error Details</p>
                    If you email ezClocker support for assistance please provide any
                    information in the box below with your message.
                    <textarea
                        rows="4"
                        class="ez-faded-error-details ezFullWidthEditor ezTopMargin_2">
                        -----------------------------------------
                        | Error Stack Trace (for the ezClocker team)
                        -----------------------------------------
                        ${EzDialog.ezInstance.ezGenerateErrorStackTraceDetails()}
                    </textarea>
                </div>`;
        }

        return em;
    }

    /**
     * @public
     * @function
     * Attempts to generate a stack trace from an empty JSONObject target.
     * @returns {string}
     */
    ezGenerateErrorStackTraceDetails() {
        const targetObject = new EzException('Stack trace exception');

        return EzObject.hasProperty(targetObject, 'stack')
            ? targetObject['stack']
            : new Error().stack;
    }

    /**
     * @public
     * @function
     * Generates a url for the provided imgFileName to use in dialog UX
     * @returns {string}
     */
    ezGetDialogImgUrl(imgFileName) {
        return EzString.hasLength(imgFileName)
            ? globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicImagesUrl(imgFileName)
            : EzDialog.ezInstance.ezDialogImageUrls.infoImageUrl;
    }

    /**
     * @public
     * @function
     * Display a generic dialog for a service error response.
     * @param {undefined|null|string} eTitle
     * Default: 'EzClocker Error'
     * @param {undefined|null|string} userErrorMessage
     * Default: EzDialog.ezInstance.ezBuildUnhandledErrorMessage(false);
     * @param {undefined|null|string} eResponse
     * Default: null
     * @param {undefined|null|string} eAdditionalDetails
     * Default: EzString.EMPTY
     * @param {undefined|null|boolean} includeStackTrace
     * Default is true
     * @param {undefined|null|number} optionalWidth
     * Default is: EzDialogSizes.widths.w1024
     * @param {undefined|null|number} optionalHeight
     * Default is: EzDialogSizes.heights.h500
     * @returns {Promise.resolve}
     */
    ezShowServiceResponseErrorDialog(
        eTitle = 'EzClocker Service Error',
        userErrorMessage = EzDialog.ezInstance.ezBuildUnhandledErrorMessage(false),
        eResponse = null,
        eAdditionalDetails = EzString.EMPTY,
        includeStackTrace = true,
        optionalWidth = EzDialogSizes.widths.w1024,
        optionalHeight = EzDialogSizes.heights.h500) {

        if (!EzString.hasLength(userErrorMessage)) {
            userErrorMessage = EzDialog.ezInstance.ezBuildUnhandledErrorMessage(false);
        }

        let errorResponse = EzString.EMPTY;

        let eResponseJson = EzString.EMPTY;

        if (EzObject.isValid(eResponse)) {
            const eResponseMessage = EzString.hasLength(eResponse.message)
                ? `Error reported: ${eResponse.message}`
                : EzString.EMPTY;

            const errorCodeMsg = EzObject.isValid(eResponse.errorCode)
                ? `Error code: ${eResponse.errorCode}`
                : EzString.EMPTY;

            errorResponse = EzString.hasLength(eResponseMessage) || EzString.hasLength(errorCodeMsg)
                ? EzHtml.build`
                    -----------------------------------------
                    | Error Response
                    -----------------------------------------
                    ${eResponseMessage}
                    ${errorCodeMsg}`
                : EzString.EMPTY;

            eResponseJson = EzHtml.build`
                -----------------------------------------
                | Error Response Data
                -----------------------------------------
                ${EzJson.toJson(eResponse, true)}`;
        }

        eAdditionalDetails = EzString.hasLength(eAdditionalDetails)
            ? EzHtml.build`
                -----------------------------------------
                | Additional Details
                -----------------------------------------
                ${eAdditionalDetails}`
            : EzString.EMPTY;

        const eStackTrace = EzBoolean.isTrue(includeStackTrace)
            ? EzHtml.build`
                -----------------------------------------
                | Error Stack Trace (for the ezClocker team)
                -----------------------------------------
                ${EzDialog.ezInstance.ezGenerateErrorStackTraceDetails()}`
            : EzString.EMPTY;

        const eDetails = EzHtml.build`
            <div
                class="ez-faded-error-details">
                <p>Error Details</p>
                If you email ezClocker support for assistance please provide any information
                in the box below with your message.
                <textarea
                    rows="22"
                    cols="60"
                    class="ez-error-message-details ezFullWidthEditor ezTopMargin_2">
                    ${errorResponse.trim()}
                    ${eAdditionalDetails.trim()}
                    ${eResponseJson.trim()}
                    ${eStackTrace.trim()}
                </textarea>
            </div>`;

        globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

        const dialogId = 'EzServiceResponseErrorDialog';

        // Remove any existing dialog
        if (globalThis.ezApi.ezElementExists(dialogId)) {
            EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);
        }

        return EzPromise.asyncAction(
            (finished) => {
                const ezDialogConfig = new EzDialogConfig(dialogId);

                ezDialogConfig.title = eTitle;

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <div
                            id="${dialogId}"
                            class="ezClockerDialog">
                            ${userErrorMessage}
                            ${eDetails}
                        </div>`);

                ezDialogConfig.ezDialogIconUrl = EzDialog.ezInstance.ezGetDialogImgUrl(EzDialog.ezInstance.ezDialogImageUrls.errorImageUrl);

                ezDialogConfig.width = EzNumber.numberOrDefault(optionalWidth, EzDialogSizes.widths.w1024);

                ezDialogConfig.height = EzNumber.numberOrDefault(optionalHeight, EzDialogSizes.heights.h500);

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_OKButton`,
                        text: 'OK',
                        click: () => EzDialog.ezInstance.ezCloseDialog(dialogId)
                    }
                ];

                ezDialogConfig.close = () => {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);
                    return finished();
                };

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    dialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Closes, destroys and removes the JQuery dialog associated with the provided dialogId
     * @param {string} dialogId
     */
    ezCloseDestroyAndRemoveDialog(dialogId) {
        if (!EzString.hasLength(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog);
        }

        if (EzUx.elementExists(dialogId)) {
            EzUx.$id(dialogId).dialog('destroy')

            EzUx.$id(dialogId).remove();

            if (EzUx.elementExists(dialogId)) {
                EzUx.removeElement(dialogId);
            }
        }
    }

    /**
     * @public
     * @function
     * Displays an error message dialog with an ok button.
     * @param {undefined|null|string} title
     * Default: 'EzClocker Error Message'
     * @param {string} eMessage
     * @param {undefined|null|string} details
     * Default: EzString.EMPTY
     * @param {undefined|null|*} optionalData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default is: Default value for property width on EzDialogConfig
     * @param {undefined|null|number} optionalHeight
     * Default is: Default value for property height on EzDialogConfig
     * @returns {Promise.resolve}
     * A resolve only promise.
     */
    ezShowErrorDialog(title, eMessage, details, optionalData, optionalWidth, optionalHeight) {
        if (!EzString.hasLength(eMessage)) {
            throw new EzBadParamException(
                'eMessage',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezShowErrorDialog);
        }

        return EzPromise.asyncAction(
            (finished) => {
                if (!EzString.hasLength(eMessage)) {
                    // No message to display, simply return the optionalData
                    return finished(optionalData);
                }

                // Remove any existing dialog
                if (EzUx.elementExists(EzDialog.ezInstance.EZ_ERROR_DIALOG_ID)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(EzDialog.ezInstance.EZ_ERROR_DIALOG_ID);
                }

                const ezDialogConfig = EzClockerDialog.ezCreateDialogConfig(EzDialog.ezInstance.EZ_ERROR_DIALOG_ID);

                ezDialogConfig.title = EzString.hasLength(title)
                    ? title
                    : 'EzClocker Error';

                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${EzDialog.ezInstance.EZ_ERROR_DIALOG_ID}_OKButton`,
                        text: 'OK',
                        click: () => EzDialog.ezInstance.ezCloseDialog(EzDialog.ezInstance.EZ_ERROR_DIALOG_ID)
                    }
                ];

                ezDialogConfig.close = () => {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(EzDialog.ezInstance.EZ_ERROR_DIALOG_ID);

                    return finished();
                };

                ezDialogConfig.ezDialogIconUrl = '/public/images/icons/alert-dark-red.svg';

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <div
                            id="${EzDialog.ezInstance.EZ_ERROR_DIALOG_ID}"
                            class="ezClockerDialog">
                            <div
                                id="${EzDialog.ezInstance.EZ_ERROR_DIALOG_ID}_MessageContainer">
                                ${eMessage}
                            </div>
                            <div
                                id="${EzDialog.ezInstance.EZ_ERROR_DIALOG_ID}_DetailsContainer"
                                class="ez-error-message-textarea-container"
                                ${EzString.hasLength(details) ? EzString.EMPTY : 'style="display:none"'}>
                                <div>
                                <textarea
                                    id="id="${EzDialog.ezInstance.EZ_ERROR_DIALOG_ID}_Details"
                                        class="ez-error-message-details"
                                        rows="4'>
                                    ${EzString.stringOrEmpty(details)}
                                </textarea>
                            </div>
                            </div>
                        </div>`);

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    EzDialog.ezInstance.EZ_ERROR_DIALOG_ID,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    EzDialog.ezInstance.EZ_ERROR_DIALOG_ID,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Inject's a dynamic dialog's HTML into the EzInjectedDialogContainer's div.
     * @param {string} dialogHtml
     */
    ezInjectDialogUx(dialogHtml) {
        if (!EzString.hasLength(dialogHtml)) {
            throw new EzBadParamException(
                'dialogHtml',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezInjectDialogUx);
        }

        if (!EzUx.elementExists(EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID)) {
            // EzUx.appendContent(
            EzUx.appendContent(
                'body',
                EzHtml.build`
                    <div
                        id="${EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID}"
                        style="display:none">
                    </div>`);
        }

        EzUx.appendContent(
            EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID,
            dialogHtml);
    }

    /**
     * TODO: Remove this method once all use is migrated
     * @public
     * @function
     * Inserts a OK message dialog into the page and displays it. Reuses the existing dialog if already inserted.
     * @param message
     * @param resultFunction
     * Returns value selected in result
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDialog.ezShowOK(title, message, passThroughData, optionalWidth, optionalHeight)
     */
    ezShowMessage(title, message, onCloseCallback, passThroughData) {
        return EzDialog.ezInstance.ezShowOK(
            title,
            message,
            passThroughData,
            null,
            null)
            .then(
                (...args) => {
                    if (EzFunction.isFunction(onCloseCallback)) {
                        return onCloseCallback(args)
                    }
                });
    }

    /**
     * TODO: Remove this method once all use is migrated
     * @public
     * @function
     * Inserts a OK message dialog into the page and displays it. Reuses the existing dialog if already inserted.
     * @param {undefined|null|string} title
     * Default is: 'EzClocker Message'
     * @param {string} message
     * @param {undefined|null|*} passThroughData
     * Default is: null
     * @param {undefined|null|number} width
     * Default is: Default value for property width on EzDialogConfig
     * @param {undefined|null|number} height
     * Default is: Default value for property height on EzDialogConfig
     * @param {undefined|null|function} onCloseCallback
     * Default is: null
     * @param {undefined|null|boolean} forceStopWait
     * Default is: false
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDialog.ezShowOK(title, message, passThroughData, optionalWidth, optionalHeight)
     */
    ezShowOKMessage(title, message, passThroughData, width, height, onCloseCallback, forceStopWait) {
        if (EzBoolean.isTrue(forceStopWait)) {
            globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();
        }

        return EzDialog.ezInstance.ezShowOK(
            title,
            message,
            passThroughData,
            width,
            height)
            .then(
                (...args) => {
                    if (EzFunction.isFunction(onCloseCallback)) {
                        return onCloseCallback(args)
                    }
                });
    }

    /**
     * @public
     * @function
     * Inserts a OK message dialog into the page and displays it. Reuses the existing dialog if already inserted.
     * @param {undefined|null|string} title
     * Default: 'EzClocker Message'
     * @param {string} message
     * @param {undefined|null|*} passThroughData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default: Default width for EzDialogConfig
     * @param {undefined|null|number} optionalHeight
     * Default: Default height for EzDialogConfig
     * @param {undefined|null|boolean} optionalForceStopSpinner
     * Default: false
     * @param resultFunction
     * Returns value selected in result
     * @returns {Promise.resolve}
     */
    ezShowNonModalOKMessage(title, message, passThroughData, optionalWidth, optionalHeight, optionalForceStopSpinner) {
        if (!EzString.hasLength(message)) {
            throw new EzBadParamException(
                'message',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezShowNonModalOKMessage);
        }

        return EzPromise.asyncAction(
            (resolve) => {
                const ezNonModalOKMessageDialogId = 'EzNonModalOKMessageDialogId';

                if (EzUx.elementExists(ezNonModalOKMessageDialogId)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(ezNonModalOKMessageDialogId);
                }

                const ezDialogConfig = new EzDialogConfig(ezNonModalOKMessageDialogId);

                ezDialogConfig.title = title;

                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzNumber.isNumber(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${ezNonModalOKMessageDialogId}_OkButton`,
                        text: 'OK',
                        click: () => EzDialog.ezInstance.ezCloseDialog(ezNonModalOKMessageDialogId)
                    }
                ];

                ezDialogConfig.close = () => {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(ezNonModalOKMessageDialogId);

                    return resolve(passThroughData);
                };

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <div
                            id="${ezNonModalOKMessageDialogId}"
                            class="ezClockerDialog">
                            <table
                                id="${ezNonModalOKMessageDialogId}_LayoutTable"
                                class="ezClockerDialogContent">
                                <tr>
                                    <td
                                        id="${ezNonModalOKMessageDialogId}_IconImageContainer"
                                        class="ezClockerDialogImageCell">
                                        <img
                                            id="${ezNonModalOKMessageDialogId}_IconImage"
                                            class="ezClockerDialogImage"
                                            src="${EzDialog.ezInstance.ezDialogImageUrls.infoImageUrl}"/>
                                    </td>
                                    <td
                                        id="${ezNonModalOKMessageDialogId}_ContentContainer"
                                        class="ezClockerDialogMessageCell">
                                        ${message}
                                    </td>
                                </tr>
                            </table>
                        </div>`);

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    ezNonModalOKMessageDialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    ezNonModalOKMessageDialogId,
                    EzBoolean.booleanOrTrue(optionalForceStopSpinner))
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Inserts a OK message dialog into the page and displays it. Reuses the existing dialog if already inserted.
     * @param {undefined|null|string} title
     * Default: 'EzClocker Message'
     * @param {undefined|null|string} message
     * Default: EzString.EMPTY
     * @param {undefined|null|*} passThroughData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default: Default value for EzDialogConfig width property
     * @param {undefined|null|number} optionalHeight
     * Default: Default value for EzDialogConfig height property
     * @returns {Promise.resolve}
     */
    ezShowOK(title, message, passThroughData, optionalWidth, optionalHeight) {
        return EzPromise.asyncAction(
            (finished) => {
                const dialogId = 'EzOkDialog';

                if (EzUx.elementExists(dialogId)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);
                }

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <div
                            id="${dialogId}">
                            <table
                                id="${dialogId}LayoutTable"
                                class="ezClockerDialogContent">
                                <tr>
                                    <td
                                        id="${dialogId}ImageContainer"
                                        class="ezClockerDialogImageCell">
                                        <img
                                            id="${dialogId}Image"
                                            class="ezClockerDialogImage"
                                            src="${EzDialog.ezInstance.ezDialogImageUrls.infoImageUrl}"/>
                                    </td>
                                    <td
                                        id="${dialogId}MessageContainer" class="ezClockerDialogMessageCell">
                                        ${EzString.stringOrEmpty(message)}
                                    </td>
                                </tr>
                            </table>
                        </div>`);

                const ezDialogConfig = new EzDialogConfig(dialogId);

                ezDialogConfig.title = EzString.stringOrDefault(title, 'EzClocker Message');

                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzNumber.isNumber(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_OKButton`,
                        text: 'OK',
                        click: () => EzDialog.ezInstance.ezCloseDialog(dialogId)
                    }
                ];

                ezDialogConfig.close = () => {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

                    return finished(passThroughData);
                };

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    dialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Inserts a OK message dialog into the page and displays it. Reuses the existing dialog if already inserted.
     * @param {undefined|null|string} customDialogId
     * Default: 'EzOkDialogCustomClose'
     * @param {undefined|null|string} title
     * Default: 'EzClocker Message'
     * @param {undefined|null|string} message
     * Default: EzString.EMPTY
     * @param {undefined|null|*} passThroughData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default: Default value for EzDialogConfig width property
     * @param {undefined|null|number} optionalHeight
     * Default: Default value for EzDialogConfig height property
     * @returns {Promise.resolve}
     * The promise resolve is the handler for the click of the OK button and does not close the dialog. The dialog must
     * be closed manually.
     */
    ezShowOKCustomClose(customDialogId = 'EzOkDialogCustomClose', title = 'EzClocker Message', message = EzString.EMPTY, passThroughData = null,
        optionalWidth = null, optionalHeight = null) {
        return EzPromise.asyncAction(
            (finished) => {
                const dialogId = EzString.stringWithTextOrDefault(
                    customDialogId,
                    'EzOkDialogCustomClose');

                if (EzUx.elementExists(dialogId)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);
                }

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <div
                            id="${dialogId}">
                            <table
                                id="${dialogId}_LayoutTable"
                                class="ezClockerDialogContent">
                                <tr
                                    id="${dialogId}_ContentContainer">
                                    <td
                                        id="${dialogId}_ImageContainer"
                                        class="ezClockerDialogImageCell">
                                        <img
                                            id="${dialogId}_Image"
                                            class="ezClockerDialogImage"
                                            src="${EzDialog.ezInstance.ezDialogImageUrls.infoImageUrl}"/>
                                    </td>
                                    <td
                                        id="${dialogId}_MessageContainer" class="ezClockerDialogMessageCell">
                                        ${EzString.stringOrEmpty(message)}
                                    </td>
                                </tr>
                            </table>
                        </div>`);

                const ezDialogConfig = new EzDialogConfig(dialogId);

                ezDialogConfig.title = EzString.stringOrDefault(title, 'EzClocker Message');

                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzNumber.isNumber(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_OKButton`,
                        text: 'OK',
                        click: () => finished(passThroughData, dialogId)
                    }
                ];

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    dialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Inserts a OK message dialog with the warning icon into the page and displays it.
     * Reuses the existing dialog if already inserted.
     * @param {undefined|null|string} title
     * Default: 'EzClocker Message'
     * @param {undefined|null|string} message
     * Default: EzString.EMPTY
     * @param {undefined|null|*} passThroughData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default: Default value for EzDialogConfig width property
     * @param {undefined|null|number} optionalHeight
     * Default: Default value for EzDialogConfig height property
     * @returns {Promise.resolve}
     */
    ezShowWarning(title, message, passThroughData, optionalWidth, optionalHeight) {
        return EzPromise.asyncAction(
            (finished) => {
                const dialogId = 'EzWarningDialog';

                if (EzUx.elementExists(dialogId)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);
                }

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <div
                            id="${dialogId}">
                            <table
                                id="${dialogId}LayoutTable"
                                class="ezClockerDialogContent">
                                <tr>
                                    <td
                                        id="${dialogId}ImageContainer"
                                        class="ezClockerDialogImageCell">
                                        <img
                                            id="${dialogId}Image"
                                            class="ezClockerDialogImage"
                                            src="${EzDialog.ezInstance.ezDialogImageUrls.warnImageUrl}"/>
                                    </td>
                                    <td
                                        id="${dialogId}MessageContainer" class="ezClockerDialogMessageCell">
                                        ${EzString.stringOrEmpty(message)}
                                    </td>
                                </tr>
                            </table>
                        </div>`);

                const ezDialogConfig = new EzDialogConfig(dialogId);

                ezDialogConfig.title = EzString.stringOrDefault(title, 'EzClocker Warning');

                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzNumber.isNumber(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_OKButton`,
                        text: 'OK',
                        click: () => EzDialog.ezInstance.ezCloseDialog(dialogId)
                    }
                ];

                ezDialogConfig.close = () => {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

                    return finished(passThroughData);
                };

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    dialogId,
                    ezDialogConfig);

                globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Inserts a OK message dialog into the page and displays it. Reuses the existing dialog if already inserted.
     * @param {undefined|null|string} title
     * Default: 'Accept or Decline'
     * @param {undefined|null|string} message
     * Default: EzString.EMPTY
     * @param {undefined|null|*} passThroughData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default: Default value for EzDialogConfig width property
     * @param {undefined|null|number} optionalHeight
     * Default: Default value for EzDialogConfig height property
     * @returns {Promise.resolve}
     */
    ezShowAgreeDeclineMessageDialog(title, message, passThroughData, optionalWidth, optionalHeight) {
        return EzPromise.asyncAction(
            (finished) => {
                const dialogId = 'EzAgreeDeclineMessageDialog';

                if (EzUx.elementExists(dialogId)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);
                }

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <style id="${dialogId}_Styles">
                            .hideTopRightCloseButton .ui-dialog-titlebar-close {
                                display: none;
                            }
                        </style>
                        <div
                            id="${dialogId}"
                            title="${EzString.stringOrDefault(title, 'Accept or Decline')}">
                            <table
                                id="${dialogId}_LayoutTable"
                                class="ezClockerDialogContent">
                                <tr>
                                    <td
                                        id="${dialogId}_ImageContainer"
                                        class="ezClockerDialogImageCell">
                                        <img
                                            id="${dialogId}_Image"
                                            class="ezClockerDialogImage"
                                            src="${EzDialog.ezInstance.ezDialogImageUrls.infoImageUrl}"/>
                                    </td>
                                    <td
                                        id="${dialogId}_MessageContainer"
                                        class="ezClockerDialogMessageCell">
                                        ${EzString.stringOrEmpty(message)}
                                    </td>
                                </tr>
                            </table>
                        </div>`);

                const ezDialogConfig = new EzDialogConfig(dialogId);

                ezDialogConfig.title = EzString.stringOrDefault(title, 'Accept or Decline');

                ezDialogConfig.dialogClass = `${ezDialogConfig.dialogClass} hideTopRightCloseButton`;

                ezDialogConfig.closeOnEscape = false;

                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzNumber.isNumber(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_AcceptButton`,
                        text: 'Agree',
                        click: () => {
                            EzDialog.ezInstance.ezCloseDialog(dialogId);

                            return finished(
                                EzDialogResponse.build(
                                    dialogId,
                                    EzDialogResponseStatus.AGREE,
                                    passThroughData));
                        }
                    },
                    {
                        id: `${dialogId}_DeclineButton`,
                        text: 'Decline',
                        click: () => {
                            EzDialog.ezInstance.ezCloseDialog(dialogId);

                            return finished(
                                EzDialogResponse.build(
                                    dialogId,
                                    EzDialogResponseStatus.DECLINE,
                                    passThroughData));
                        }
                    }
                ];

                ezDialogConfig.open = (_event, ui) => {
                    // Remove the actual button
                    $('.hideTopRightCloseButton', ui).remove();
                };

                ezDialogConfig.close = () => EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

                EzDialog.ezInitDialogWithConfig(
                    dialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Inserts a OK message dialog into the page and displays it. Reuses the existing dialog if already inserted.
     * @param {undefined|null|string} title
     * Default: 'EzClocker Message'
     * @param {undefined|null|string} message
     * Default: EzString.EMPTY
     * @param {undefined|null|function} onCloseCallback
     * Default: null
     * @param {undefined|null|*} passThroughData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default: Default value for EzDialogConfig width property
     * @param {undefined|null|number} optionalHeight
     * Default: Default value for EzDialogConfig height property
     * @param {undefined|null|boolean} useOldResponse
     * Default: false
     * @param resultFunction
     * Returns value selected in result
     * @returns {Promise.resolve}
     */
    ezOkCancelMessage(title, message, onCloseCallback, passThroughData, optionalWidth, optionalHeight, useOldResponse) {
        return EzPromise.asyncAction(
            (finished) => {
                const dialogId = 'EzOkCancelDialog';

                if (EzUx.elementExists(dialogId)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);
                }

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <div
                            id="${dialogId}"
                            title="${EzString.stringOrDefault(title, 'EzClocker Message')}">
                            <table
                                id="${dialogId}_LayoutTable"
                                class="ezClockerDialogContent">
                                <tr>
                                    <td
                                        id="${dialogId}_IconImageContainer"
                                        class="ezClockerDialogImageCell">
                                        <img
                                            id="${dialogId}_IconImage"
                                            class="ezClockerDialogImage"
                                            src="${EzDialog.ezInstance.ezDialogImageUrls.questionImageUrl}"/>
                                    </td>
                                    <td
                                        id="${dialogId}_MessageContainer"
                                        class="ezClockerDialogMessageCell">
                                        ${EzString.stringOrEmpty(message)}
                                    </td>
                                </tr>
                            </table>
                        </div>`);

                const ezDialogConfig = new EzDialogConfig(dialogId);

                if (EzObject.isValid(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzObject.isValid(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_OkButton`,
                        text: 'OK',
                        click: () => {
                            EzDialog.ezInstance.ezCloseDialog(dialogId);

                            if (globalThis.ezApi.isFunction(onCloseCallback)) {
                                if (EzBoolean.isTrue(useOldResponse)) {
                                    onCloseCallback('OK');
                                } else {
                                    onCloseCallback(EzDialogResponse.OK, passThroughData);
                                }
                            }

                            return finished(EzDialogResponse.OK, passThroughData);
                        }
                    },
                    {
                        id: `${dialogId}_CancelButton`,
                        text: 'Cancel',
                        click: () => {
                            EzDialog.ezInstance.ezCloseDialog(dialogId);

                            if (globalThis.ezApi.isFunction(onCloseCallback)) {
                                if (globalThis.ezApi.isTrue(useOldResponse)) {
                                    onCloseCallback('CANCEL');
                                } else {
                                    onCloseCallback(EzDialogResponse.CANCEL, passThroughData);
                                }
                            }

                            return finished(EzDialogResponse.CANCEL, passThroughData);
                        }
                    }
                ];

                ezDialogConfig.close = () => EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    dialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Inserts a OK message dialog into the page and displays it. Reuses the existing dialog if already inserted.
     * @param {undefined|null|string} title
     * Default: 'EzClocker Message'
     * @param {undefined|null|string} message
     * Default: EzString.EMPTY
     * @param {undefined|null|function} onCloseCallback
     * Default: null
     * @param {undefined|null|*} passThroughData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default: Default value for EzDialogConfig width property
     * @param {undefined|null|number} optionalHeight
     * Default: Default value for EzDialogConfig height property
     * @returns {Promise.resolve}
     */
    ezConfirmationDialog(title, message, passThroughData, optionalWidth, optionalHeight) {
        return EzPromise.asyncAction(
            (finished) => {
                const dialogId = 'EzConfirmationDialog';

                if (EzUx.elementExists(dialogId)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);
                }

                EzUx.appendContent(
                    'body',
                    EzHtml.build`
                        <div
                            id="${dialogId}"
                            title="${EzString.stringOrDefault(title, 'Confirm')}">
                            <table
                                id="${dialogId}_LayoutTable"
                                class="ezClockerDialogContent">
                                <tr>
                                    <td
                                        id="${dialogId}_IconImageContainer"
                                        class="ezClockerDialogImageCell">
                                        <img
                                            id="${dialogId}_IconImage"
                                            class="ezClockerDialogImage"
                                            src="${EzDialog.ezInstance.ezDialogImageUrls.questionImageUrl}"/>
                                    </td>
                                    <td
                                        id="${dialogId}_MessageContainer"
                                        class="ezClockerDialogMessageCell">
                                        ${EzString.stringOrEmpty(message)}
                                    </td>
                                </tr>
                            </table>
                        </div>`);

                const ezDialogConfig = new EzDialogConfig(dialogId);

                if (EzObject.isValid(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                if (EzObject.isValid(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${dialogId}_ConfirmationButton`,
                        text: 'OK',
                        click: () => {
                            EzDialog.ezInstance.ezCloseDialog(dialogId);

                            return finished(EzDialogResponse.CONFIRM, passThroughData);
                        }
                    },
                    {
                        id: `${dialogId}_CancelButton`,
                        text: 'Cancel',
                        click: () => {
                            EzDialog.ezInstance.ezCloseDialog(dialogId);

                            return finished(EzDialogResponse.CANCEL, passThroughData);
                        }
                    }
                ];

                ezDialogConfig.close = () => EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    dialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    dialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Displays an OK and Cancel dialog
     * @param {undefined|null|string} title
     * Default: 'EzClocker Message'
     * @param {undefined|null|string} message
     * Default: EzString.EMPTY
     * @param {undefined|null|*} passThroughData
     * Default: null
     * @param {undefined|null|number} optionalWidth
     * Default: Default value for EzDialogConfig width property
     * @param {undefined|null|number} optionalHeight
     * Default: Default value for EzDialogConfig height property
     * @returns {Promise.resolve}
     */
    ezOkCancel(title, message, passThroughData, optionalWidth, optionalHeight) {
        return EzPromise.asyncAction(
            (finished) => {
                globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

                const ezOkCancelDialogId = 'EzOkCancelDialog';

                if (EzUx.elementExists(ezOkCancelDialogId)) {
                    EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(ezOkCancelDialogId);
                }

                EzUx.appendContent(
                    EzHtml.build`
                        <div
                            id="${ezOkCancelDialogId}"
                            title="${EzString.stringOrDefault(title, 'EzClocker Message')}">
                            <table
                                id="${ezOkCancelDialogId}_LayoutTable"
                                class="ezClockerDialogContent">
                                <tr>
                                    <td
                                        id="${ezOkCancelDialogId}_ImageCell"
                                        class="ezClockerDialogImageCell">
                                        <img
                                            id="${ezOkCancelDialogId}_Image"
                                            class="ezClockerDialogImage"
                                            src="${EzDialog.ezInstance.ezDialogImageUrls.infoImageUrl}"/>
                                    </td>
                                    <td
                                        id="${ezOkCancelDialogId}_DialogMessage"
                                        class="ezClockerDialogMessageCell">
                                        ${EzString.stringOrEmpty(message)}
                                    </td>
                                </tr>
                            </table>
                        </div>`);

                const ezDialogConfig = new EzDialogConfig(ezOkCancelDialogId);

                if (EzNumber.isNumber(optionalHeight)) {
                    ezDialogConfig.height = optionalHeight;
                }
                if (EzNumber.isNumber(optionalWidth)) {
                    ezDialogConfig.width = optionalWidth;
                }

                ezDialogConfig.buttons = [
                    {
                        id: `${ezOkCancelDialogId}_OkButton`,
                        text: 'OK',
                        click: () => {
                            EzDialog.ezInstance.ezCloseDialog(ezOkCancelDialogId);

                            return finished(
                                {
                                    ezDialogResponse: EzDialogResponse.OK,
                                    ezPassThroughData: passThroughData
                                });
                        }
                    },
                    {
                        id: `${ezOkCancelDialogId}_CancelButton`,
                        text: 'Cancel',
                        click: () => {
                            EzDialog.ezInstance.ezCloseDialog(ezOkCancelDialogId);

                            return finished(
                                {
                                    ezDialogResponse: EzDialogResponse.CANCEL,
                                    ezPassThroughData: passThroughData
                                });
                        }
                    }
                ];

                ezDialogConfig.close = () => EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(ezOkCancelDialogId);

                EzDialog.ezInstance.ezInitDialogWithConfig(
                    ezOkCancelDialogId,
                    ezDialogConfig);

                return EzDialog.ezInstance.ezShowDialog(
                    ezOkCancelDialogId,
                    true)
                    .then(EzPromise.ignoreResolve);
            });
    }

    /**
     * @public
     * @function
     * Performs the dialog button's click action (if available) and then closes the dialog.
     * @param {string} aDialogId
     * @param {Function|null} originalClick
     */
    ezHandleCustomButtonClick(aDialogId, originalClick) {
        if (!EzString.hasLength(aDialogId)) {
            throw new EzBadParamException(
                'aDialogId',
                EzDialog.ezInstance,
                EzDialog.ezInstance.ezHandleCustomButtonClick);
        }

        return () => {
            if (EzFunction.isFunction(originalClick)) {
                originalClick();
            }

            if (EzUx.elementExists(aDialogId)) {
                globalThis.ezApi.ezclocker.ezCloseDialog(aDialogId);
            }
        };
    }

    /**
     * @public
     * @function
     * Injects a pop-up information dialog that allows the caller to specify the action buttons.
     * @param {string} title
     * @param {string} content
     * @param {Array} customButtons
     * Example of customButtons object: customButtons = [{
     *     id: "MyDialogButtonId",
     *     text: "Button Text",
     *     click: function() { // Handler for the click of the button }
     * }]
     * @param {string} [parentContainerId='EzHideDialogsDiv']
     * @param {String|Number|null} [width='auto']
     * @param {String|Number|null} [height='auto']
     * @param {undefined|null|string} [dialogId='EzUnnamedActionDialog']
     * @returns {Promise.resolve}
     *
     */
    ezActionDialog(title, content, customButtons, parentContainerId = 'EzHideDialogsDiv', width = 'auto', height = 'auto', dialogId = 'EzUnnamedActionDialog') {
        dialogId = EzString.hasLength(dialogId)
            ? dialogId
            : `EzActionDialog`;

        parentContainerId = EzString.hasLength(parentContainerId)
            ? parentContainerId
            : '_HideDialogsDiv';

        customButtons = EzArray.isArray(customButtons)
            ? customButtons
            : [
                {
                    id: `${dialogId}_OKButton`,
                    text: 'OK',
                    click: EzDialog.ezInstance.ezCloseDialog(dialogId)
                }
            ];

        globalThis.ezApi.ezclocker.ezUi.ezAppendContent(
            parentContainerId,
            EzHtml.build`
                <div
                    id="${dialogId}"
                    class="ezClockerDialog">
                    <table
                        id="${dialogId}_LayoutTable"
                        class="ezClockerDialogContent">
                        <tr>
                            <td
                                id="${dialogId}_IconCell"
                                class="ezClockerDialogImageCell">
                                <img
                                    id="${dialogId}_Icon"
                                    class="ezClockerDialogImage"
                                    src="${EzDialog.ezInstance.ezDialogImageUrls.infoImageUrl}"/>
                            </td>
                            <td
                                id="${dialogId}_ContentCell"
                                class="ezClockerDialogMessageCell">
                                ${EzString.stringOrEmpty(content)}
                            </td>
                        </tr>
                    </table>
                </div>`);

        const ezDialogConfig = new EzDialogConfig(dialogId);

        ezDialogConfig.title = title;

        ezDialogConfig.buttons = customButtons;

        if ('auto' === width || EzNumber.isNumber(width)) {
            ezDialogConfig.width = width;
        }

        if ('auto' === height || EzNumber.isNumber(height)) {
            ezDialogConfig.height = height;
        }

        ezDialogConfig.close = () => EzDialog.ezInstance.ezCloseDestroyAndRemoveDialog(dialogId);

        EzDialog.ezInitDialogWithConfig(
            dialogId,
            ezDialogConfig);

        return EzDialog.ezInstance.ezShowDialog(
            dialogId,
            true)
            .then(EzPromise.ignoreResolve);
    }

    /**
     * @public
     * @function
     * Shows a non-modal ok dialog with the tip text.
     * @param {undefined|null|string} title
     * Default is: 'EzClocker Message'
     * @param {undefined|null|string} tipType
     * Accepted values: EzDialog.ezInstance.EMPLOYEE_INVITE_TIP,  EzDialog.ezInstance.EMPLOYEE_PIN_TIP
     * @returns {Promise.resolve}
     */
    ezShowTip(title, tipType) {
        if (tipType === EzDialog.ezInstance.EMPLOYEE_INVITE_TIP) {
            return EzDialog.ezInstance.ezShowNonModalOKMessage(
                title,
                EzDialog.ezInstance.getEmployeeInviteTip());
        }
        if (tipType === EzDialog.ezInstance.EMPLOYEE_PIN_TIP) {
            return EzDialog.ezInstance.ezShowNonModalOKMessage(
                title,
                EzDialog.ezInstance.getEmployeePINNumberTip());
        }

        throw new EzBadParamException(
            'tipType',
            EzDialog.ezInstance,
            EzDialog.ezInstance.showTip);
    }

    /**
     * TODO: Remove this method once all use is migrated
     * @public
     * @function
     * Shows a non-modal ok dialog with the tip text.
     * @param {undefined|null|string} title
     * Default is: 'EzClocker Message'
     * @param {undefined|null|string} tipType
     * Accepted values: EzDialog.ezInstance.EMPLOYEE_INVITE_TIP,  EzDialog.ezInstance.EMPLOYEE_PIN_TIP
     * @returns {Promise.resolve}
     * @deprecated
     * Will get removed in a future release.
     * Migrate to: globalThis.ezApi.ezclocker.ezDialog.ezShowTip(title, tipType)
     */
    showTip(title, tipType) {
        return EzDialog.ezInstance.ezShowTip(title, tipType);
    }

    /**
     * @public
     * @function
     * Generates employee invitation tip HTML
     * @returns {string}
     */
    getEmployeeInviteTip() {
        return EzHtml.build`
            <p><strong>Inviting Employees to ezClocker</strong></p><p>When you invite an employee, they’ll get an email or text with a link to set up their ezClocker account. Once they create a password, they can log in and start tracking time from the mobile app or website.</p><ol><li data-list="bullet"><span class="ql-ui" contenteditable="false"></span><strong>Invite expiration:</strong> Links expire after 24 hours. If needed, you can resend the invite from your employer dashboard.</li><li data-list="bullet"><span class="ql-ui" contenteditable="false"></span><strong>New users only:</strong> Invites work only for employees without an existing ezClocker account. If the email or phone number is already in use, you’ll need to enter a different one.</li></ol>
            `;
    }

    /**
     * @public
     * @function
     * Generates employee PIN number tip html.
     * @returns {string}
     */
    getEmployeePINNumberTip() {
        return EzHtml.build`
            <p
                class="font-size:12pt;">
                If you want to use one device for your employees at a job site then you can use our
                ezClocker Kiosk App built for the tablets. Employees can sign in using a 4 digit PIN number.
                The employer can also use the app to view employees’ timesheets, adjust time entries and email
                time clocks.
            </p>`;
    }

    /**
     * TODO: Remove method after all use is migrated to a different solution
     * @public
     * @function
     * @param {string} _imagePrefix
     * @param {string} message
     * @param {undefined|null|function} resultFunction
     * @param {string} title
     * @param {undefined|null|number} dialogWidth
     * @param {undefined|null|number} dialogHeight
     * @returns {Promise.resolve}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDialog.ezYesNoDialog(title, message, onCloseCallback, optionalWidth, optionalHeight, useOldResponse)
     */
    ezClockerYesNoDialog(_imagePrefix, message, resultFunction, title, dialogWidth, dialogHeight) {
        globalThis.ezApi.ezclocker.ezUi.ezForceStopPageWait();

        globalThis.ezApi.ezclocker.ezLogger.dep(
            'EzDialog.ezInstance.ezClockerYesNoDialog',
            'EzDialog.ezInstance.ezClockerYesNo()',
            'ezclocker-dialog-helper.js');

        return EzDialog.ezInstance.ezYesNoDialog(
            title,
            message,
            resultFunction,
            dialogWidth,
            dialogHeight,
            true);
    }

    /**
     * @public
     * @function
     * Gets the id the element that is used to hold injected dialog HTML
     * @returns {string}
     */
    ezGetInjectedDialogParentId() {
        if (EzUx.elementExists(EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID)) {
            return EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID;
        }

        // Legacy parent ids
        if (EzUx.elementExists('EzHiddenDialogs')) {
            return 'EzHiddenDialogs';
        }

        if (EzUx.elementExists('EzHiddenDialogs')) {
            return 'EzHiddenDialogs';
        }

        if (EzUx.elementExists('_HideDialogsDiv')) {
            return '_HideDialogsDiv';
        }

        return EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID;
    }

    /**
     * TODO: Remove this method once all use has migrated
     * @public
     * @function
     * Displays an error dialog, relating to error results from API calls.
     * @param {undefined|null|object} eResponse
     * @param {undefined|null|string} eTitle
     * @param {undefined|null|string} eMessage
     * @param {undefined|null|string} userMessage
     * @param {undefined|null|*} optionalData
     * @returns {Promise.resolve}
     * @deprecated
     * Migrate to one of the following:
     *     1) globalThis.ezApi.ezclocker.ezDialog.ezShowServiceResponseErrorDialog(
     *         eTitle,
     *         userErrorMessage,
     *         eResponse,
     *         eAdditionalDetails,
     *         includeStackTrace,
     *         eDialogOptionalWidth,
     *         eDialogOptionalHeight)
     *     2) globalThis.ezApi.ezclocker.ezDialog.ezShowErrorWithDetails(
     *         eTitle,
     *         eMessage,
     *         eDetails);
     */
    ezShowApiResponseError(eResponse, eTitle, eMessage, userMessage, optionalData) {
        if (!EzString.hasLength(eTitle)) {
            eTitle = 'EzClocker Unexpected Error';
        }

        const eDetails = EzObject.isValid(eResponse)
            ? `[Error details: ${EzJson.toJson(eResponse)}]`
            : EzString.EMPTY;

        if (!EzString.hasLength(eMessage)) {
            eMessage = EzObject.isValid(eResponse)
                ? `[Error: ${eResponse.message}]`
                : EzString.EMPTY;
        }

        const fullErrorMessage = EzString.hasLength(userMessage)
            ? `${userMessage} ${eMessage}`
            : eMessage;

        globalThis.ezApi.ezclocker.ezLogger.error(`${fullErrorMessage} [Details: ${eDetails}]`);

        return EzDialog.ezInstance.ezShowErrorDialog(
            eTitle,
            fullErrorMessage,
            eDetails,
            optionalData);
    }

    /**
     * @public
     * @function
     * Builds the UX html to display additional details for a message/error message that
     * users can copy + paste when contacting support.
     * @param {string} additionalDetails
     * @returns {string}
     */
    ezBuildAdditionalDetailsContainerHTML(additionalDetails) {
        return EzHtml.build`
            <div
                id="EzAdditionalDetailsContainer"
                class="ez-faded-error-details">
                <label
                    for="EzAdditionalDetailsTextArea">
                    Additional Details
                </label>
                <div
                    id="EzAdditionalDetailsTextAreaContainer">
                    <textarea
                        id="EzAdditionalDetailsTextArea"
                        class="ez-error-message-details"
                        rows="8">
                        ${EzString.stringOrDefault(additionalDetails, 'No additional details available.')}
                    </textarea>
                </div>
                Please provide the dialog's message and the additional details above if you contact ezClocker support about this message.
            </div>`;
    }

    /**
     * @public
     * @function
     * Displays an error dialog with optional details box.
     * @param {undefined|null|string} eTitle
     * @param {undefined|null|string} eMessage
     * @param {undefined|null|string} eDetails
     * @returns {Promise.resolve}
     */
    ezShowErrorWithDetails(eTitle, eMessage, eDetails) {
        if (!EzString.hasLength(eTitle)) {
            eTitle = 'EzClocker Unexpected Error';
        }

        if (!EzString.hasLength(eMessage)) {
            eMessage = 'EzClocker encounted an unexpected error while performing the requested action.';
        }

        if (!EzString.hasLength(eDetails)) {
            eDetails = 'No additional details provided.';
        }

        return EzDialog.ezInstance.ezShowErrorDialog(eTitle, eMessage, eDetails);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * Read-only property: Gets the name of this class's singleton instance when registered with globalThis.ezApi.Gets the name of this class's singleton
     * instance when registered with globalThis.ezApi.
     * @static
     * @public
     * @readonly
     * @function
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezDialog';
    }

    /**
     * Read-only property: Gets an object of event names that this class may trigger.
     * @static
     * @public
     * @readonly
     * @function
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzDialog_Ready'
        };
    }

    /**
     * Stores the singleton instance of this class that was created by and registerd with globalThis.ezApi.
     * @static
     * @private
     * @property {EzDialog} ezInstance
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzDialog.ezApiName]
        ? globalThis.ezApi.ezclocker[EzDialog.ezApiName]
        : null;

    /**
     * Getter for static private property: EzDialog.#ezInstance
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @static
     * @public
     * @function
     * @returns {EzDialog}
     */
    static get ezInstance() {
        return EzDialog.#ezInstance;
    }

    /**
     * Setter for static private property: EzDialog.#ezInstance
     * Sets the singleton instance of of this class that is registered with globalThis.ezApi.
     * @static
     * @public
     * @function
     * @param {EzDialog} ezDialog
     */
    static set ezInstance(ezDialog) {
        if (null != EzDialog.#ezInstance) {
            throw new Error('EzDialog\'s singleton instance is already reigstered with globalThis.ezApi.');
        }

        EzDialog.#ezInstance = ezDialog;
    }

    /**
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @static
     * @private
     * @property {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzDialog.ezApiName]?.ezApiRegistrationState
        ? EzRegistrationState.REGISTERED
        : null;

    /**
     * Getter for property: EzDialog.#ezApiRegistrationState
     * Gets the ezApi registration state of this classes's singleton instance.
     * @static
     * @public
     * @function
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzDialog.#ezApiRegistrationState;
    }

    /**
     * Setter: Sets the ezApi registration state of this classes's singleton instance.
     * @static
     * @public
     * @function
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzDialog.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        const canRegister = EzRegistrationState.PENDING === EzDialog.ezApiRegistrationState &&
            globalThis?.ezApi?.ready && globalThis.ezApi?.ezclocker &&
            globalThis.ezApi.ezclocker?.[EzUI.ezApiName]?.ready &&
            globalThis.ezApi.ezclocker?.[EzDateTime.ezApiName]?.ready;

        if (EzBoolean.isTrue(canRegister)) {
            globalThis.ezApi.ezclocker.ezLogger.debug('EzDialog is ready to register with ezApi.');
        }

        return canRegister;
    }

    /**
     * Read-only getter: Gets if this class's singleton instance is registered with ezApi yet.
     * @static
     * @private
     * @readonly
     * @function
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzDialog.ezInstance &&
            EzRegistrationState.REGISTERED === EzDialog.ezApiRegistrationState;
    }

    /**
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     * @static
     * @private
     * @function
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (!EzDialog.#ezIsRegistered && EzDialog.#ezCanRegister) {
            globalThis.ezApi.ezRegisterNewApi(EzDialog, EzDialog.ezApiName);
        }

        return EzDialog.#ezIsRegistered;
    }

    /**
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzDialog.ezApiName
     *     2) Property getter EzDialog.ezEventNames
     *     3) Property getter EzDialog.ezInstance
     *     4) Property setter EzDialog.ezInstance
     *     5) Property getter EzDialog.ezApiRegistrationState
     *     6) Property setter EzDialog.ezApiRegistrationState
     *     7) Property getter EzDialog.#ezCanRegister
     *     8) Property getter EzDialog.#ezIsRegistered
     *     9) Method EzDialog.#ezRegistrator()
     * @static
     */
    static {
        if (!EzDialog.#ezIsRegistered) {
            EzDialog.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzDialog.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzDialog.ezOnEzApiReadyEventName,
                    EzDialog.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzUI.ezEventNames.onReady,
                    EzDialog.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzDateTime.ezEventNames.onReady,
                    EzDialog.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}

export {
    EzDialogSizes,
    /**
     * TODO: Remove export of EzDialogConfig once all use migrated to new import
     * @deprecated
     * Will remove in a future release
     * Migrate to using new import location:
     *     import { EzDialogConfig } from '/ezlibrary/ux/dialogs/EzDialogConfig.js';
     */
    EzDialogConfig,
    /**
     * TODO: Remove export of EzDialogResponseStatus once all use migrated to new import
     * @deprecated
     * Will remove in a future release
     * Migrate to using new import location:
     *     import { EzDialogResponse } from '/ezlibrary/ux/dialogs/EzDialogResponse.js';
     */
    EzDialogResponseStatus,
    /**
     * TODO: Remove export of EzDialogResponse once all use migrated to new import
     * @deprecated
     * Will remove in a future release
     * Migrate to using new import location:
     *     import { EzDialogResponse } from '/ezlibrary/ux/dialogs/EzDialogResponse.js';
     */
    EzDialogResponse,
    EzDialog
};

